import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SuggestionsDropdown from './SuggestionsDropdown';
import './EditDocument.css';

const EditDocument = ({ documentId, onClose, onDocumentUpdated }) => {
  const { token, user, isAuthenticated, loading: authLoading, getAuthHeaders: authGetAuthHeaders } = useAuth();
  const [document, setDocument] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buttonStates, setButtonStates] = useState({
    reject: false,
    approve: false
  });
  const [error, setError] = useState(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(50); // Percentage width for preview section
  const [isResizing, setIsResizing] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    documentInfo: false,
    payrollForm: false,
    metadataHistory: false,
    correctionComments: false
  });
  const [sectionHeights, setSectionHeights] = useState({
    documentInfo: 'auto',
    payrollForm: 'auto',
    metadataHistory: 'auto',
    correctionComments: 'auto'
  });
  const [resizingSection, setResizingSection] = useState(null);
  const resizeStartY = useRef(null);
  const resizeStartHeight = useRef(null);
  const previewLoadAttempted = useRef(false);
  const rulesAppliedForDocument = useRef(null); // Track which document had rules applied

  // Suggestions state for contractor/project/job title dropdowns
  const [suggestions, setSuggestions] = useState({
    contractorName: { visible: false, items: [], originalValue: '' },
    subcontractorName: { visible: false, items: [], originalValue: '' },
    projectName: { visible: false, items: [], originalValue: '' },
    jobTitle: {} // Will be populated with employeeIndex keys: {0: {visible: false, items: [], originalValue: ''}, 1: {...}, ...}
  });

  // Track original values for manual rule creation
  const [originalFieldValues, setOriginalFieldValues] = useState({
    contractorName: '',
    subcontractorName: '',
    projectName: '',
    jobTitle: ''
  });

  // Proactive validation state for multiple matches
  const [proactiveValidationErrors, setProactiveValidationErrors] = useState(new Set());

  // Track rule replacements for user notifications
  const [ruleReplacements, setRuleReplacements] = useState({
    contractorName: null,
    subcontractorName: null,
    projectName: null,
    jobTitle: {} // Will be populated with employeeIndex keys: {0: ruleInfo, 1: ruleInfo, ...}
  });

  // Debouncing refs to prevent rapid suggestion toggles
  const suggestionTimeouts = useRef({});
  const suggestionDebounceDelay = 300; // 300ms debounce
  
  // Track which suggestions are currently being processed to prevent conflicts
  const suggestionsBeingProcessed = useRef(new Set());
  
  // Track programmatic updates to prevent clearing rule notifications during rule application
  const programmaticUpdates = useRef(new Set());
  
  // Track validated fields to prevent infinite validation loops
  const validatedFields = useRef(new Map());

  // Use the AuthContext's getAuthHeaders function for consistency
  const getAuthHeaders = authGetAuthHeaders;

  // Helper function to toggle section collapse state
  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Vertical resize handlers for sections
  const handleSectionResizeStart = (sectionName, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingSection(sectionName);
    resizeStartY.current = e.clientY;

    // Get current height
    const sectionElement = e.target.closest('.form-section').querySelector('.section-content');
    if (sectionElement) {
      resizeStartHeight.current = sectionElement.offsetHeight;
    }

    document.addEventListener('mousemove', handleSectionResizeMove);
    document.addEventListener('mouseup', handleSectionResizeEnd);
  };

  const handleSectionResizeMove = (e) => {
    if (!resizingSection || resizeStartY.current === null) return;

    const deltaY = e.clientY - resizeStartY.current;
    const newHeight = Math.max(100, resizeStartHeight.current + deltaY); // Minimum 100px

    setSectionHeights(prev => ({
      ...prev,
      [resizingSection]: `${newHeight}px`
    }));
  };

  const handleSectionResizeEnd = () => {
    setResizingSection(null);
    resizeStartY.current = null;
    resizeStartHeight.current = null;
    document.removeEventListener('mousemove', handleSectionResizeMove);
    document.removeEventListener('mouseup', handleSectionResizeEnd);
  };

  // Double-click to reset section height to auto
  const handleSectionResizeDoubleClick = (sectionName) => {
    setSectionHeights(prev => ({
      ...prev,
      [sectionName]: 'auto'
    }));
  };

  // Helper function to format error messages consistently
  const formatErrorMessage = (err, prefix = 'Error') => {
    console.error(`${prefix}:`, err);
    console.error('Full error object:', {
      message: err.message,
      response: err.response,
      data: err.response?.data,
      status: err.response?.status
    });

    let errorMessage = '';
    let rawMessage = '';

    if (err.response?.data?.detail) {
      // FastAPI validation error format
      if (typeof err.response.data.detail === 'string') {
        rawMessage = err.response.data.detail;
      } else if (Array.isArray(err.response.data.detail)) {
        // Pydantic validation errors are arrays
        rawMessage = err.response.data.detail.map(e =>
          `${e.loc?.join('.') || 'unknown'}: ${e.msg || e.message || 'validation error'}`
        ).join(', ');
      } else {
        try {
          rawMessage = JSON.stringify(err.response.data.detail, null, 2);
        } catch {
          rawMessage = 'Unable to display error details';
        }
      }
    } else if (err.response?.data) {
      // Handle case where data is an object
      if (typeof err.response.data === 'string') {
        rawMessage = err.response.data;
      } else if (typeof err.response.data === 'object') {
        try {
          rawMessage = JSON.stringify(err.response.data, null, 2);
        } catch {
          rawMessage = 'Unable to display error details';
        }
      } else {
        rawMessage = String(err.response.data);
      }
    } else if (err.message) {
      rawMessage = err.message;
    } else {
      rawMessage = 'Unknown error occurred';
    }

    // Try to parse validation errors for better formatting
    const parsedErrors = parseValidationErrors(rawMessage);
    if (parsedErrors) {
      errorMessage = formatValidationErrors(parsedErrors);
    } else {
      errorMessage = {
        type: 'simple',
        message: `${prefix}: ${rawMessage}`
      };
    }

    return errorMessage;
  };

  // Helper function to parse validation error messages into structured format
  const parseValidationErrors = (message) => {
    if (!message || typeof message !== 'string') return null;

    // Debug: log the incoming message to understand its format

    // Check if this is a validation error message (be more flexible with patterns)
    const isValidationError = message.includes('validation issues found') ||
                              message.includes('issues that need attention') ||
                              message.includes('validation failed') ||
                              message.includes('validation error') ||
                              message.includes('found these contracts:') ||
                              message.includes('Company name') ||
                              message.includes('Project name') ||
                              message.includes('Employee') ||
                              (message.includes('error') && (message.includes('contractor') || message.includes('project')));

    if (!isValidationError) return null;

    const errors = {
      summary: '',
      issueCount: 0,
      employees: [],
      issues: {},
      rawDetails: [] // Store raw error details for better display
    };

    // Extract issue count (handle both old and new formats)
    let countMatch = message.match(/with (\d+) validation issues? found/);
    if (!countMatch) {
      countMatch = message.match(/encountered (\d+) issues? that need attention/);
    }
    if (countMatch) {
      errors.issueCount = parseInt(countMatch[1]);
    }

    // Try to extract specific error details using multiple approaches
    const extractSpecificErrors = (text) => {
      const specificErrors = [];

      // Look for contract/project errors
      if (text.includes('found these contracts:')) {
        const contractSection = text.split('found these contracts:')[1];
        if (contractSection) {
          specificErrors.push('Project name is ambiguous. ' + contractSection.split('\n')[0]);
        }
      }

      // Look for company name errors
      if (text.includes('Company name') && text.includes('clarification')) {
        specificErrors.push('Company name needs clarification - check contractor/subcontractor fields');
      }

      // Look for employee errors
      const employeeMatches = text.match(/Employee [^:]+: [^.]+/g);
      if (employeeMatches) {
        specificErrors.push(...employeeMatches);
      }

      // Look for field-specific errors
      const fieldErrors = [
        { pattern: /Project name [^.]+/gi, description: 'Project field issue' },
        { pattern: /Company name [^.]+/gi, description: 'Company field issue' },
        { pattern: /Contractor [^.]+/gi, description: 'Contractor field issue' },
        { pattern: /Multiple contracts [^.]+/gi, description: 'Project selection issue' }
      ];

      fieldErrors.forEach(({ pattern, description }) => {
        const matches = text.match(pattern);
        if (matches) {
          specificErrors.push(...matches);
        }
      });

      return specificErrors;
    };

    errors.rawDetails = extractSpecificErrors(message);

    // Check for document-level errors first (contractor/project validation)
    const documentLevelErrorPatterns = [
      /Contractor validation error[^.]*\.?/gi,
      /Project validation error[^.]*\.?/gi,
      /Contractor '[^']*' not found/gi,
      /Project '[^']*' not found/gi,
      /Company name [^.]*\.?/gi,
      /Project name [^.]*\.?/gi,
      /Multiple contractors found/gi,
      /Multiple projects found/gi
    ];

    let hasDocumentLevelErrors = false;
    documentLevelErrorPatterns.forEach(pattern => {
      const matches = message.match(pattern);
      if (matches) {
        hasDocumentLevelErrors = true;
        matches.forEach(match => {
          let categoryName;
          if (match.toLowerCase().includes('contractor') || match.toLowerCase().includes('company')) {
            categoryName = 'Contractor/Subcontractor Name Issues';
          } else if (match.toLowerCase().includes('project')) {
            categoryName = 'Project Name Issues';
          } else {
            categoryName = 'Other Issues'; // Fallback for unmatched patterns
          }

          if (!errors.issues[categoryName]) {
            errors.issues[categoryName] = [];
          }

          const isDuplicate = errors.issues[categoryName].some(issue => issue.message === match.trim());
          if (!isDuplicate) {
            errors.issues[categoryName].push({
              employee: 'Document',
              message: match.trim()
            });
          }
        });
      }
    });

    // If we found document-level errors and no employee-specific patterns, don't process as employee errors
    if (hasDocumentLevelErrors && !message.includes('Employee ') && !message.includes('employees[')) {
      return errors;
    }

    // Split by "Employee" or bullet points to parse each employee's errors
    let parts;
    if (message.includes('Employee ')) {
      parts = message.split(/Employee /);
    } else {
      // New format with bullet points
      const lines = message.split('\n');
      const errorLines = lines.filter(line => line.trim().startsWith('•'));
      parts = ['', ...errorLines.map(line => line.replace('•', '').trim())];
    }

    parts.forEach((part, index) => {
      if (index === 0) {
        // First part contains the summary
        errors.summary = part.trim();
      } else {
        // Extract employee name and error details
        const nameMatch = part.match(/^([^:]+):/);
        if (nameMatch) {
          const employeeName = nameMatch[1].trim();
          const errorText = part.substring(nameMatch[0].length).trim();

          // Check if this is a document-level error (contractor/project) vs employee-specific error
          const isDocumentLevelError = errorText.includes('Multiple contracts found') ||
                                     errorText.includes('Project name needs clarification') ||
                                     errorText.includes('Project name is ambiguous') ||
                                     errorText.includes('Multiple contractors found') ||
                                     errorText.includes('Company name needs clarification') ||
                                     errorText.includes('Company name is ambiguous') ||
                                     errorText.includes('Contractor validation error') ||
                                     errorText.includes('Project validation error') ||
                                     (errorText.includes('not found') && (errorText.includes('contractor') || errorText.includes('project')));

          // Only add to employees array if this is actually an employee-specific error
          if (!isDocumentLevelError) {
            errors.employees.push(employeeName);
          }

          // Parse specific error types (handle both old and new formats)
          // For document-level errors (project/contractor), only show once regardless of employee count
          if (errorText.includes('Multiple contracts found') || errorText.includes('Project name needs clarification') || errorText.includes('Project name is ambiguous')) {
            if (!errors.issues['Project Name Issues']) {
              errors.issues['Project Name Issues'] = [];
            }
            // Check if we already have this project error to avoid duplicates
            const isDuplicate = errors.issues['Project Name Issues'].some(issue => issue.message === errorText);
            if (!isDuplicate) {
              errors.issues['Project Name Issues'].push({
                employee: 'Document', // Use "Document" instead of employee name for document-level issues
                message: errorText
              });
            }
          } else if (errorText.includes('Multiple contractors found') || errorText.includes('Company name needs clarification') || errorText.includes('Company name is ambiguous')) {
            if (!errors.issues['Contractor/Subcontractor Name Issues']) {
              errors.issues['Contractor/Subcontractor Name Issues'] = [];
            }
            // Check if we already have this company error to avoid duplicates
            const isDuplicate = errors.issues['Contractor/Subcontractor Name Issues'].some(issue => issue.message === errorText);
            if (!isDuplicate) {
              errors.issues['Contractor/Subcontractor Name Issues'].push({
                employee: 'Document', // Use "Document" instead of employee name for document-level issues
                message: errorText
              });
            }
          } else if (errorText.includes('not found')) {
            // Check if this is a document-level "not found" error (contractor/project) vs employee-level
            const isDocumentLevelNotFound = errorText.includes('contractor') || errorText.includes('project') || errorText.includes('Contractor validation error') || errorText.includes('Project validation error');

            if (isDocumentLevelNotFound) {
              // Document-level not found errors
              const categoryName = errorText.includes('contractor') ? 'Contractor/Subcontractor Name Issues' : 'Project Name Issues';
              if (!errors.issues[categoryName]) {
                errors.issues[categoryName] = [];
              }
              const isDuplicate = errors.issues[categoryName].some(issue => issue.message === errorText);
              if (!isDuplicate) {
                errors.issues[categoryName].push({
                  employee: 'Document',
                  message: errorText
                });
              }
            } else {
              // Employee-level not found errors
              if (!errors.issues['Not Found']) {
                errors.issues['Not Found'] = [];
              }
              errors.issues['Not Found'].push({
                employee: employeeName,
                message: errorText
              });
            }
          } else if (errorText.includes('cannot be empty')) {
            if (!errors.issues['Empty Fields']) {
              errors.issues['Empty Fields'] = [];
            }
            errors.issues['Empty Fields'].push({
              employee: employeeName,
              message: errorText
            });
          } else {
            if (!errors.issues['Other']) {
              errors.issues['Other'] = [];
            }
            errors.issues['Other'].push({
              employee: employeeName,
              message: errorText
            });
          }
        }
      }
    });

    // Extract user corrections if present
    const correctionsMatch = message.match(/User corrections: (.+)$/);
    if (correctionsMatch) {
      errors.userCorrections = correctionsMatch[1];
    }


    // If we detected this as a validation error but didn't extract any specific issues,
    // use rawDetails or the original message to provide better information
    if (errors.issueCount > 0 && Object.keys(errors.issues).length === 0 && errors.employees.length === 0) {
      if (errors.rawDetails.length > 0) {
        // Use the specific errors we extracted, but deduplicate document-level errors
        const uniqueDetails = [...new Set(errors.rawDetails)];
        errors.issues['Validation Issues'] = uniqueDetails.map(detail => ({
          employee: 'System',
          message: detail
        }));
      } else {
        // Fall back to showing key parts of the original message
        const messageParts = message.split('\n').filter(line =>
          line.trim().length > 0 &&
          !line.includes('validation issues found') &&
          !line.includes('issues that need attention') &&
          !line.includes('Generated by using')
        );

        if (messageParts.length > 0) {
          // Deduplicate message parts since project/contractor errors apply to whole document
          const uniqueParts = [...new Set(messageParts.map(part => part.trim()))];
          errors.issues['Validation Issues'] = uniqueParts.slice(0, 5).map(part => ({
            employee: 'System',
            message: part
          }));
        } else {
          // Final fallback
          errors.issues['Validation Issues'] = [{
            employee: 'System',
            message: `${errors.issueCount} validation issue${errors.issueCount > 1 ? 's' : ''} detected. Please review the form fields highlighted in red.`
          }];
        }
      }
    }

    return errors;
  };

  // Helper function to format structured validation errors for display
  const formatValidationErrors = (parsedErrors) => {
    if (!parsedErrors) return null;

    return {
      type: 'validation',
      parsedData: parsedErrors
    };
  };

  // Helper function to format review comments in structured format like validation errors
  const renderFormattedComments = (comments) => {
    if (!comments || typeof comments !== 'string') return comments;

    // Filter out JSON array displays from User corrections
    let filteredComments = comments;
    
    // Remove JSON array displays that start with [ and end with ]
    const userCorrectionsPattern = /User corrections: \[.*?\](?=\s|$)/g;
    filteredComments = filteredComments.replace(userCorrectionsPattern, '').trim();
    
    // If the entire comment was just the JSON array, return null to hide it
    if (!filteredComments || filteredComments === 'User corrections:') {
      return null;
    }

    // Try to parse the comments as validation errors
    const parsedErrors = parseValidationErrors(filteredComments);

    if (parsedErrors && typeof parsedErrors === 'object') {
      // Render structured validation error format
      return (
        <div className="structured-comments">
          <div><strong>📋 Validation Errors</strong></div>
          <div className="result-details">
            {parsedErrors.issueCount && parsedErrors.issueCount > 0 && (
              <div>⚠️ {parsedErrors.issueCount} validation issue{parsedErrors.issueCount > 1 ? 's' : ''} found</div>
            )}
          </div>

          {parsedErrors.employees && parsedErrors.employees.length > 0 && (
            <div className="localhost-info">
              <span className="info-icon">👥</span>
              <div className="info-content">
                <div className="info-title">Employees Affected:</div>
                <div className="employee-list">
                  {parsedErrors.employees.map((employee, index) => (
                    <div key={index} className="employee-name">{employee}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {parsedErrors.issues && Object.keys(parsedErrors.issues).length > 0 && Object.entries(parsedErrors.issues).map(([categoryTitle, categoryErrors], index) => (
            <div key={index} className="localhost-info">
              <span className="info-icon">❌</span>
              <div className="info-content">
                <div className="info-title">{categoryTitle}:</div>
                <div className="error-list">
                  {categoryErrors && categoryErrors.map((errorItem, errorIndex) => (
                    <div key={errorIndex} className="error-item">
                      • {errorItem.employee}: {errorItem.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // If not a validation error, return filtered comments
    return filteredComments;
  };

  // Helper function to extract detailed validation errors from document reviews
  const extractDetailedValidationErrors = () => {
    if (!documentReviews || documentReviews.length === 0) return null;

    // Find the most recent review with validation errors
    const recentReview = documentReviews.find(review => {
      const status = review.new_status || review.status;
      return (status === 'review_required' || status === 'error') &&
             review.comments &&
             (review.comments.includes('validation issues found') ||
              review.comments.includes('issues that need attention'));
    });

    if (!recentReview || !recentReview.comments) return null;

    return parseValidationErrors(recentReview.comments);
  };

  // Helper function to handle ProcessingResult responses consistently
  const handleProcessingResult = (result, successAction, prefix = 'operation') => {

    // Check if this is actually a success even if success flag is false
    const messageStr = typeof result.message === 'string' ? result.message : '';
    const isActuallySuccessful = result.success ||
      messageStr.includes('completed successfully') ||
      messageStr.includes('ready for approval') ||
      messageStr.includes('validation successful') ||
      messageStr.includes('data validated');

    if (isActuallySuccessful) {
      const actionMessage = successAction || prefix;
      const displayMessage = messageStr || `Document ${actionMessage} successfully! Status: ${result.status}`;

      // Display success message in the inline upload-result instead of popup
      setError({
        type: 'success',
        message: displayMessage
      });

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);

      // Force validation state reset on success - use multiple methods to ensure it takes effect
      setValidationTrigger(prev => prev + 1);

      // Also clear validation cache and force immediate re-render
      setTimeout(() => {
        setValidationTrigger(prev => prev + 1);
      }, 100);

      return true; // Success
    } else {
      // Handle actual error case
      let errorMessage = '';

      if (typeof result.message === 'string') {
        // Try to parse validation errors for better formatting
        let parsedErrors = parseValidationErrors(result.message);

        // If we didn't get detailed errors from the API response but we have document reviews,
        // try to extract validation errors from the most recent review
        if (!parsedErrors || (parsedErrors.issueCount > 0 && Object.keys(parsedErrors.issues).length <= 1)) {
          const detailedErrors = extractDetailedValidationErrors();
          if (detailedErrors) {
            parsedErrors = detailedErrors;
          }
        }

        if (parsedErrors) {
          errorMessage = formatValidationErrors(parsedErrors);
        } else {
          // Not a validation error, use regular formatting
          errorMessage = {
            type: 'simple',
            message: `Document ${prefix} failed: ${result.message}`
          };
        }
      } else if (typeof result.message === 'object') {
        try {
          errorMessage = {
            type: 'simple',
            message: `Document ${prefix} failed: ${JSON.stringify(result.message, null, 2)}`
          };
        } catch {
          errorMessage = {
            type: 'simple',
            message: `Document ${prefix} failed: Unable to display error details`
          };
        }
      } else {
        errorMessage = {
          type: 'simple',
          message: `Document ${prefix} failed: ${String(result.message || 'Unknown error')}`
        };
      }

      console.error(`${prefix} failed:`, result);
      setError(errorMessage);
      return false; // Error
    }
  };
  const [formData, setFormData] = useState({
    title: '',
    status: '',
    form_type: '',
    uploader_id: '',
    external_reference_id: ''
  });
  const [correctionComments, setCorrectionComments] = useState('');
  const [savingComments, setSavingComments] = useState(false); // Track if correction comments are being saved
  const [metadataFormData, setMetadataFormData] = useState({});
  const [metadataChanged, setMetadataChanged] = useState(false); // Track if metadata has been modified
  const [documentReviews, setDocumentReviews] = useState([]);
  const [validationTrigger, setValidationTrigger] = useState(0); // Force re-render for validation updates
  const [validationCache, setValidationCache] = useState(new Map()); // Cache validation results to track changes
  const [reviewHistoryExpanded, setReviewHistoryExpanded] = useState(false);

  // Auto-calculation state
  const [showCalculationPrompt, setShowCalculationPrompt] = useState(false);
  const [calculationContext, setCalculationContext] = useState(null);
  const [hasBeenPromptedForCalculation, setHasBeenPromptedForCalculation] = useState(false);
  const [autoCalculateEnabled, setAutoCalculateEnabled] = useState(false);

  // Helper function to format last4ssn with leading zeros
  const formatLast4SSN = (value) => {
    if (!value && value !== 0) return '';

    // Handle negative numbers early
    if (typeof value === 'number' && value < 0) return '';

    // Convert to string and handle decimal numbers by taking only integer part
    let stringValue = String(value);
    if (stringValue.includes('.')) {
      stringValue = stringValue.split('.')[0];
    }

    // Remove any non-digits
    const digits = stringValue.replace(/\D/g, '');

    // If empty or invalid, return empty string
    if (digits === '') return '';

    // Convert to number and back to string to remove leading zeros, then pad
    const num = parseInt(digits, 10);

    // Ensure it's a valid number and within reasonable range (0-9999)
    if (isNaN(num) || num < 0 || num > 9999) return '';

    // Pad with leading zeros to make it 4 digits
    return num.toString().padStart(4, '0');
  };

  // Resize handlers for the preview section
  const handleMouseDown = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || typeof window === 'undefined' || !window.document) return;

    // Prevent default to avoid any browser drag behaviors
    e.preventDefault();

    const container = window.document.querySelector('.edit-content-layout');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Constrain between 20% and 80%
    const constrainedWidth = Math.max(20, Math.min(80, newWidth));
    setPreviewWidth(constrainedWidth);
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing && typeof window !== 'undefined' && window.document && window.document.addEventListener) {
      window.document.addEventListener('mousemove', handleMouseMove);
      window.document.addEventListener('mouseup', handleMouseUp);

      return () => {
        if (typeof window !== 'undefined' && window.document && window.document.removeEventListener) {
          window.document.removeEventListener('mousemove', handleMouseMove);
          window.document.removeEventListener('mouseup', handleMouseUp);
        }
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Dynamic input sizing function
  const adjustInputWidth = useCallback((input) => {
    if (!input || typeof window === 'undefined') return;

    const value = input.value || input.placeholder || '';
    const minChars = 3; // Minimum character width
    const maxChars = 12; // Maximum character width to prevent overly wide inputs

    // Calculate character count, adding a buffer for decimal places and padding
    let charCount = Math.max(value.length + 1, minChars);
    charCount = Math.min(charCount, maxChars);

    // Apply the width using ch units (character width)
    input.style.width = `${charCount}ch`;
  }, []);

  // Format number to 2 decimal places
  const formatToDecimal = useCallback((value) => {
    if (!value || value === '') return '0.00';

    // Remove any non-numeric characters except decimal point and minus sign
    const cleanValue = value.toString().replace(/[^0-9.-]/g, '');

    // Parse as float
    const numValue = parseFloat(cleanValue);

    // Return formatted value if it's a valid number
    if (!isNaN(numValue)) {
      return numValue.toFixed(2);
    }

    return '0.00'; // Return default if not a valid number
  }, []);

  // Ensure numeric value is always displayed as float
  const ensureFloatDisplay = useCallback((value) => {
    if (!value || value === '' || value === 0 || value === '0') return '0.00';
    return formatToDecimal(value);
  }, [formatToDecimal]);

  // Auto-adjust input widths and format numeric values on blur
  const handleDynamicInputChange = useCallback((e, originalHandler) => {
    // Adjust width first
    adjustInputWidth(e.target);

    // Then call the original handler if provided
    if (originalHandler) {
      originalHandler(e);
    }
  }, [adjustInputWidth]);

  // Handle numeric input formatting on blur (when user finishes typing)
  const handleNumericInputBlur = useCallback((e, originalHandler, employeeIndex, fieldName) => {
    const input = e.target;
    const isNumericInput = input.classList.contains('legacy-daily-hours') ||
                          input.classList.contains('legacy-hours-input') ||
                          input.classList.contains('legacy-rate-input') ||
                          input.classList.contains('legacy-wages-input') ||
                          input.classList.contains('legacy-deduction') ||
                          input.classList.contains('legacy-benefit-input') ||
                          input.classList.contains('legacy-gross-other') ||
                          input.classList.contains('legacy-net-pay') ||
                          input.classList.contains('legacy-ot-wages-input') ||
                          input.classList.contains('legacy-std-wages-input') ||
                          input.classList.contains('legacy-deduction-fica') ||
                          input.classList.contains('legacy-deduction-witholdingTax') ||
                          input.classList.contains('legacy-deduction-other') ||
                          input.classList.contains('legacy-deduction-healthInsurance') ||
                          input.classList.contains('legacy-deduction-retirement_401k');

    if (isNumericInput) {
      // Format the value (empty becomes "0.00")
      const formattedValue = formatToDecimal(input.value);

      if (formattedValue !== input.value) {
        // Create a synthetic event with the formatted value
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: formattedValue
          }
        };

        // Update the input value
        input.value = formattedValue;

        // Call original handler with formatted value
        if (originalHandler) {
          originalHandler(syntheticEvent);
        }

        // Adjust width after formatting
        adjustInputWidth(input);
      }

      // After formatting, check if we should show the calculation prompt
      // Only for daily hours fields and if user hasn't been prompted yet
      if (employeeIndex !== undefined && fieldName &&
          isNumericalField(fieldName) &&
          formattedValue !== '' &&
          !isNaN(parseFloat(formattedValue)) &&
          !hasBeenPromptedForCalculation) {

        const updatedEmployees = metadataFormData.employees || [];
        const employee = updatedEmployees[employeeIndex];

        if (employee) {
          setCalculationContext({
            employeeIndex,
            field: fieldName,
            value: formattedValue,
            employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
          });
          setShowCalculationPrompt(true);
        }
      } else if (employeeIndex !== undefined && fieldName &&
                 isNumericalField(fieldName) &&
                 formattedValue !== '' &&
                 !isNaN(parseFloat(formattedValue)) &&
                 autoCalculateEnabled) {
        // If auto-calculate is enabled, apply calculations automatically
        applyCalculatedValues(employeeIndex);
      }
    }
  }, [formatToDecimal, adjustInputWidth, hasBeenPromptedForCalculation, autoCalculateEnabled, metadataFormData.employees]);

  // Helper function to create props for numeric inputs with auto-formatting
  const getNumericInputProps = useCallback((value, onChangeHandler, employeeIndex, fieldName) => ({
    type: "text",
    value: value || '', // Allow empty values during editing, don't force "0.00"
    onChange: (e) => handleDynamicInputChange(e, onChangeHandler),
    onBlur: (e) => handleNumericInputBlur(e, onChangeHandler, employeeIndex, fieldName),
    placeholder: "0.00"
  }), [handleDynamicInputChange, handleNumericInputBlur]);

  // Helper function to generate complete 7-day week headers from employee daysWorked data
  const generateDateHeadersFromDaysWorked = (employees, payrollStartDate) => {
    // Default fallback if no employee data
    const defaultHeaders = [
      { date: '-', day: 'Mon' },
      { date: '-', day: 'Tue' },
      { date: '-', day: 'Wed' },
      { date: '-', day: 'Thu' },
      { date: '-', day: 'Fri' },
      { date: '-', day: 'Sat' },
      { date: '-', day: 'Sun' }
    ];

    // If we have a payroll start date, use it as the basis for the week
    if (payrollStartDate) {
      try {
        const startDate = new Date(payrollStartDate);
        const headers = [];

        // Generate 7 days starting from the payroll start date
        for (let i = 0; i < 7; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i);

          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const dayName = dayNames[currentDate.getDay()];

          headers.push({
            date: currentDate.getDate().toString(),
            day: dayName
          });
        }

        return headers;
      } catch (error) {
        console.warn('Error parsing payroll start date:', payrollStartDate, error);
        // Fall through to use employee data if payroll date is invalid
      }
    }

    if (!employees || !Array.isArray(employees) || employees.length === 0) {
      return defaultHeaders;
    }

    const firstEmployee = employees[0];
    if (!firstEmployee.daysWorked || !Array.isArray(firstEmployee.daysWorked) || firstEmployee.daysWorked.length === 0) {
      return defaultHeaders;
    }

    // Find the first valid date to use as reference for calculating the week
    let referenceDate = null;
    let referenceDayOfWeek = null;

    for (const dayWorked of firstEmployee.daysWorked) {
      if (dayWorked.date && dayWorked.dayOfWeek) {
        try {
          referenceDate = new Date(dayWorked.date);
          referenceDayOfWeek = dayWorked.dayOfWeek;
          break;
        } catch (error) {
          console.warn('Error parsing date:', dayWorked.date, error);
        }
      }
    }

    if (!referenceDate || !referenceDayOfWeek) {
      return defaultHeaders;
    }

    // Map day of week codes to JavaScript day numbers (0=Sunday, 1=Monday, etc.)
    const dayMapping = {
      'SU': 0, 'M': 1, 'T': 2, 'W': 3, 'TH': 4, 'F': 5, 'S': 6
    };

    // Map grid positions to day codes (for our Mon-Sun display order)
    const gridToDayCode = ['M', 'T', 'W', 'TH', 'F', 'S', 'SU'];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const referenceDayNum = dayMapping[referenceDayOfWeek];
    if (referenceDayNum === undefined) {
      return defaultHeaders;
    }

    // Calculate the complete week starting from Monday
    const headers = [];

    // Use the employee's day-of-week data to calculate Monday
    // Map employee day codes to positions in our Monday-Sunday grid
    const dayToGridPosition = {
      'M': 0, 'T': 1, 'W': 2, 'TH': 3, 'F': 4, 'S': 5, 'SU': 6
    };

    const referencePosition = dayToGridPosition[referenceDayOfWeek];
    if (referencePosition === undefined) {
      return defaultHeaders;
    }

    // Calculate Monday's date by subtracting the reference position from the reference date
    const mondayDate = new Date(referenceDate);
    mondayDate.setDate(referenceDate.getDate() - referencePosition);

    // Generate all 7 days starting from Monday
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(mondayDate);
      currentDate.setDate(mondayDate.getDate() + i);

      headers.push({
        date: currentDate.getDate().toString(),
        day: dayNames[i]
      });
    }

    return headers;
  };

  // Helper function to parse date string in various formats
  const parseDate = (dateStr, payrollStartDate) => {
    if (!dateStr) return null;

    const dateString = String(dateStr).trim();

    // Try parsing as full date (MM/DD/YYYY or M/D/YYYY)
    const fullDatePatterns = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
      /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/
    ];

    for (const pattern of fullDatePatterns) {
      const match = dateString.match(pattern);
      if (match) {
        const month = parseInt(match[1]);
        const day = parseInt(match[2]);
        let year = parseInt(match[3]);

        // Handle 2-digit year
        if (year < 100) {
          year += 2000;
        }

        return new Date(year, month - 1, day);
      }
    }

    // If it's just a day number, infer from payroll start date
    const dayMatch = dateString.match(/^(\d{1,2})$/);
    if (dayMatch && payrollStartDate) {
      const dayNum = parseInt(dayMatch[1]);
      const startDate = new Date(payrollStartDate);

      // Try same month first
      let parsedDate = new Date(startDate.getFullYear(), startDate.getMonth(), dayNum);

      // If day is before start date, try next month
      if (parsedDate < startDate && dayNum < startDate.getDate()) {
        parsedDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, dayNum);
      }

      // Verify it's within reasonable range (within 7 days of start)
      const diffDays = Math.abs((parsedDate - startDate) / (1000 * 60 * 60 * 24));
      if (diffDays <= 7) {
        return parsedDate;
      }
    }

    return null;
  };

  // Helper function to convert daysWorked array to daily hour fields for legacy form
  const convertDaysWorkedToLegacyFormat = (employees, payrollStartDate, payrollEndDate) => {
    if (!employees || !Array.isArray(employees)) return employees;

    return employees.map(employee => {
      const convertedEmployee = { ...employee };

      // Initialize all 7 days with default values
      for (let i = 1; i <= 7; i++) {
        convertedEmployee[`standardHours_${i}`] = '0.00';
        convertedEmployee[`overtimeHours_${i}`] = '0.00';
      }

      // Format last4ssn with leading zeros
      if (convertedEmployee.last4ssn !== undefined) {
        convertedEmployee.last4ssn = formatLast4SSN(convertedEmployee.last4ssn);
      }

      // Convert daysWorked array to standardHours_X and overtimeHours_X fields
      // Map days to correct positions based on actual calendar dates
      if (employee.daysWorked && Array.isArray(employee.daysWorked) && payrollStartDate) {
        const startDate = new Date(payrollStartDate);

        employee.daysWorked.forEach((day) => {
          // Parse the date to get actual calendar date
          const dayDate = parseDate(day.date, payrollStartDate);

          if (dayDate) {
            // Calculate position within payroll week (1-based for form fields)
            const diffMs = dayDate - startDate;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const position = diffDays + 1; // 1-based index

            // Validate position is within 1-7 range
            if (position >= 1 && position <= 7) {
              const standardHours = parseFloat(day.standardHours) || 0;
              const overtimeHours = parseFloat(day.overtimeHours) || 0;
              convertedEmployee[`standardHours_${position}`] = standardHours.toFixed(2);
              convertedEmployee[`overtimeHours_${position}`] = overtimeHours.toFixed(2);
            } else {
              console.warn(`Date ${day.date} (${dayDate.toLocaleDateString()}) position ${position} is outside payroll period`);
            }
          } else {
            console.warn(`Could not parse date: ${day.date}`);
          }
        });
      }

      // Format other numeric fields with proper decimal places
      const formatCurrency = (value) => {
        const num = parseFloat(value) || 0;
        return num.toFixed(2);
      };

      const formatHours = (value) => {
        const num = parseFloat(value) || 0;
        return num.toFixed(2);
      };

      // Format wage and rate fields
      if (convertedEmployee.standardRate !== undefined) {
        convertedEmployee.standardRate = formatCurrency(convertedEmployee.standardRate);
      }
      if (convertedEmployee.overtimeRate !== undefined) {
        convertedEmployee.overtimeRate = formatCurrency(convertedEmployee.overtimeRate);
      }
      if (convertedEmployee.wages !== undefined) {
        convertedEmployee.wages = formatCurrency(convertedEmployee.wages);
      }
      if (convertedEmployee.standardWages !== undefined) {
        convertedEmployee.standardWages = formatCurrency(convertedEmployee.standardWages);
      }
      if (convertedEmployee.overtimeWages !== undefined) {
        convertedEmployee.overtimeWages = formatCurrency(convertedEmployee.overtimeWages);
      }

      // Format total hours fields
      if (convertedEmployee.standardHours !== undefined) {
        convertedEmployee.standardHours = formatHours(convertedEmployee.standardHours);
      }
      if (convertedEmployee.overtimeHours !== undefined) {
        convertedEmployee.overtimeHours = formatHours(convertedEmployee.overtimeHours);
      }

      // Format deduction fields
      if (convertedEmployee.ficaDeduction !== undefined) {
        convertedEmployee.ficaDeduction = formatCurrency(convertedEmployee.ficaDeduction);
      }
      if (convertedEmployee.withholding !== undefined) {
        convertedEmployee.withholding = formatCurrency(convertedEmployee.withholding);
      }
      if (convertedEmployee.netPay !== undefined) {
        convertedEmployee.netPay = formatCurrency(convertedEmployee.netPay);
      }

      // Format benefit rate fields
      const benefitFields = ['benefitRate1', 'benefitRate2', 'benefitRate3',
                           'hourlyPlusBenefit1', 'hourlyPlusBenefit2', 'hourlyPlusBenefit3',
                           'davisBaconMin1', 'davisBaconMin2', 'davisBaconMin3',
                           'grossOtherProjects'];
      benefitFields.forEach(field => {
        if (convertedEmployee[field] !== undefined) {
          convertedEmployee[field] = formatCurrency(convertedEmployee[field]);
        }
      });

      // Format other deduction fields
      const otherDeductionFields = ['otherDeduction1', 'otherDeduction2', 'otherDeduction3'];
      otherDeductionFields.forEach(field => {
        if (convertedEmployee[field] !== undefined) {
          convertedEmployee[field] = formatCurrency(convertedEmployee[field]);
        }
      });

      return convertedEmployee;
    });
  };

  // Helper function to convert daily hour fields back to daysWorked array
  const convertLegacyFormatToDaysWorked = (employees) => {
    if (!employees || !Array.isArray(employees)) return employees;

    console.log('[HOURS AUDIT] convertLegacyFormatToDaysWorked START');
    console.log('[HOURS AUDIT] Converting', employees.length, 'employees from legacy format to daysWorked');

    return employees.map((employee, empIndex) => {
      const convertedEmployee = { ...employee };
      const daysWorked = [];
      const employeeName = `${employee.firstName || 'Unknown'} ${employee.lastName || 'Unknown'}`;

      console.log(`[HOURS AUDIT] Employee ${empIndex + 1}: ${employeeName}`);
      console.log('[HOURS AUDIT] Input hour fields:');
      for (let i = 1; i <= 7; i++) {
        console.log(`[HOURS AUDIT]   Position ${i}: standard=${employee[`standardHours_${i}`]}, overtime=${employee[`overtimeHours_${i}`]}`);
      }

      // Convert standardHours_X and overtimeHours_X fields back to daysWorked array
      // Calculate dates based on payroll start date if available
      const payrollStartDate = metadataFormData?.payrollStartDate;
      let startDate = null;

      console.log(`[HOURS AUDIT] Payroll start date: ${payrollStartDate}`);

      if (payrollStartDate) {
        try {
          startDate = new Date(payrollStartDate);
          // Use the actual payroll start date, not Monday-aligned
          // This matches the backend's calendar position mapping where:
          // Position 1 = first day of payroll period (not necessarily Monday)
          console.log(`[HOURS AUDIT] Using actual payroll start date: ${startDate.toLocaleDateString()} (${startDate.toLocaleDateString('en-US', {weekday: 'long'})})`);
        } catch (e) {
          console.error('[HOURS AUDIT] Error parsing payroll start date:', e);
          startDate = null;
        }
      }

      for (let i = 1; i <= 7; i++) {
        const standardHours = parseFloat(employee[`standardHours_${i}`]) || 0;
        const overtimeHours = parseFloat(employee[`overtimeHours_${i}`]) || 0;

        // Always include all 7 days to preserve complete week structure
        // This prevents data loss when zero-hour days are omitted
        const dayOfWeek = ['M', 'T', 'W', 'TH', 'F', 'S', 'SU'][i - 1];

        // Calculate actual date if we have a start date
        let date = '';
        if (startDate) {
          const dayDate = new Date(startDate);
          dayDate.setDate(startDate.getDate() + (i - 1));
          date = `${(dayDate.getMonth() + 1).toString().padStart(2, '0')}/${dayDate.getDate().toString().padStart(2, '0')}/${dayDate.getFullYear()}`;
        }

        const dayEntry = {
          dayOfWeek: dayOfWeek,
          date: date,
          standardHours: standardHours,
          overtimeHours: overtimeHours
        };

        console.log(`[HOURS AUDIT]   Day ${i} (${dayOfWeek}, ${date || 'no date'}): ${standardHours}h standard, ${overtimeHours}h overtime`);

        daysWorked.push(dayEntry);

        // Remove the legacy fields from the employee object
        delete convertedEmployee[`standardHours_${i}`];
        delete convertedEmployee[`overtimeHours_${i}`];
      }

      if (daysWorked.length > 0) {
        convertedEmployee.daysWorked = daysWorked;
      }

      console.log(`[HOURS AUDIT] ${employeeName}: Created daysWorked array with ${daysWorked.length} entries`);
      console.log('[HOURS AUDIT] Final daysWorked structure:', JSON.stringify(daysWorked, null, 2));

      return convertedEmployee;
    });
  };

  // Helper function to extract validation errors from document reviews
  const extractValidationErrors = (reviews) => {
    const errors = new Set();

    if (!reviews || !Array.isArray(reviews)) return errors;

    // Define error mappings that will be used for both status transitions and main comments
    const errorMappings = [
      // Employee validation errors - removed generic patterns that apply to all employees
      // These will be handled by the more specific hasValidationError logic per employee
      { pattern: /Employee first name cannot be empty/i, fields: ['firstName'] },
      { pattern: /Employee last name cannot be empty/i, fields: ['lastName'] },

      // Contract/Project validation errors (updated for new format) - Check these FIRST for specificity
      { pattern: /Project name not found in system/i, fields: ['projectName'] },
      { pattern: /Project name needs clarification/i, fields: ['projectName'] },
      { pattern: /Project name is ambiguous/i, fields: ['projectName'] },
      { pattern: /Found these contracts:/i, fields: ['projectName'] }, // More specific pattern for multiple contracts
      { pattern: /Multiple contracts found/i, fields: ['projectName'] },
      { pattern: /Contract .+ not found/i, fields: ['projectName'] },
      { pattern: /Contract name cannot be empty/i, fields: ['projectName'] },

      // Contractor validation errors (updated for new format) - More specific patterns
      { pattern: /Contractor .+ not found/i, fields: ['contractorName'] },
      { pattern: /Contractor '.+' not found/i, fields: ['contractorName'] },
      { pattern: /No contractor found matching the specified name/i, fields: ['contractorName'] },
      { pattern: /Company name not found in system/i, fields: ['contractorName', 'subcontractorName'] },
      { pattern: /Multiple contractors found/i, fields: ['contractorName'] },
      { pattern: /Multiple contractors found for '.+'/i, fields: ['contractorName'] },
      { pattern: /Multiple contractors found - name is ambiguous/i, fields: ['contractorName'] },
      { pattern: /Company name needs clarification/i, fields: ['contractorName', 'subcontractorName'] },
      { pattern: /Company name is ambiguous/i, fields: ['contractorName', 'subcontractorName'] },
      { pattern: /Company name .+ cannot be empty/i, fields: ['contractorName', 'subcontractorName'] },
      { pattern: /Contractor validation error/i, fields: ['contractorName'] },

      // Date validation errors
      { pattern: /Week.*ending.*date.*required/i, fields: ['forWeekEnding'] },
      { pattern: /forWeekEnding.*cannot be empty/i, fields: ['forWeekEnding'] },
      { pattern: /Payroll.*start.*date/i, fields: ['payrollStartDate'] },
      { pattern: /payrollStartDate.*cannot be empty/i, fields: ['payrollStartDate'] },
      { pattern: /Payroll.*end.*date/i, fields: ['payrollEndDate'] },
      { pattern: /payrollEndDate.*cannot be empty/i, fields: ['payrollEndDate'] },

      // Job title validation errors
      { pattern: /Job title .+ not found/i, fields: ['jobTitle'] },
      { pattern: /Job title '.+' not found/i, fields: ['jobTitle'] },
      { pattern: /Job title .+ not found in system/i, fields: ['jobTitle'] },
      { pattern: /Job title '.+' not found in system/i, fields: ['jobTitle'] },
      { pattern: /Multiple job titles found/i, fields: ['jobTitle'] },
      { pattern: /Work classification cannot be empty/i, fields: ['jobTitle'] },

      // Agency validation errors
      { pattern: /Agency .+ not found/i, fields: ['contractorName'] }, // Agency linked to contractor
      { pattern: /Multiple agencies found/i, fields: ['contractorName'] },

      // General field validation
      { pattern: /Missing required fields: ([^.]+)/g, fields: [] }, // Will be parsed separately
      { pattern: /employees\[(\d+)\]\.(\w+)/g, fields: [] }, // Will be parsed separately
    ];

    // Sort reviews by date to get the chronological order
    const sortedReviews = [...reviews].sort((a, b) => new Date(a.review_date) - new Date(b.review_date));

    // Find the most recent review and check its status
    const mostRecentReview = sortedReviews[sortedReviews.length - 1];
    if (!mostRecentReview) return errors;


    // Check if the most recent review shows success - FIRST check message content, status is not reliable
    const recentStatus = mostRecentReview.new_status || mostRecentReview.status;

    // Check if the most recent review contains success messages - BUT ONLY clear errors if truly successful
    if (mostRecentReview.comments) {
      const recentComments = mostRecentReview.comments;
      const successIndicators = [
        'Document verification completed successfully',
        'all employee data validated',
        'validation successful',
        'ready for approval'
      ];

      // Only clear errors if we have success indicators AND no validation issues mentioned
      const hasValidationIssues = recentComments.includes('validation issues found') ||
                                 recentComments.includes('issues that need attention') ||
                                 recentComments.includes('not found') ||
                                 recentComments.includes('validation error');

      if (successIndicators.some(indicator => recentComments.includes(indicator)) && !hasValidationIssues) {
        return errors; // Return empty set - no validation errors
      }
    }

    // Also check the most recent status transition for success indicators
    if (mostRecentReview.status_transitions && mostRecentReview.status_transitions.length > 0) {
      const latestTransition = mostRecentReview.status_transitions[mostRecentReview.status_transitions.length - 1];
      if (latestTransition.transition_comments) {
        const transitionComments = latestTransition.transition_comments;
        const successIndicators = [
          'Document verification completed successfully',
          'all employee data validated',
          'completed successfully',
          'ready for approval',
          'validation successful',
          'data validated'
        ];

        if (successIndicators.some(indicator => transitionComments.includes(indicator))) {
          return errors; // Return empty set - no validation errors
        }
      }
    }

    // Finally check status (less reliable but still useful)
    if (recentStatus === 'processed' || recentStatus === 'processing') {
      return errors; // Return empty set
    }


    // Only extract errors from reviews that are in error states AND are recent enough to be relevant
    sortedReviews.forEach((review, index) => {
      const currentStatus = review.new_status || review.status;
      const isErrorState = currentStatus === 'error' || currentStatus === 'review_required';


      // Skip this review if it's not in an error state
      if (!isErrorState) {
        return;
      }

      // Skip old error reviews ONLY if there's a COMPLETE success after it (not partial success)
      const laterReviews = sortedReviews.slice(index + 1);
      const hasCompleteSuccess = laterReviews.some(laterReview => {
        const laterStatus = laterReview.new_status || laterReview.status;
        const laterComments = laterReview.comments || '';

        // Only consider it a complete success if status is processed AND contains success message
        const isStatusSuccess = laterStatus === 'processed';
        const hasSuccessMessage = laterComments.includes('verification completed successfully') ||
                                  laterComments.includes('data validated') ||
                                  laterComments.includes('all employee data validated');

        return isStatusSuccess && hasSuccessMessage;
      });

      // Special case: if this is the most recent review, always process it regardless of later reviews
      const isRecentReview = index === sortedReviews.length - 1;

      if (hasCompleteSuccess && !isRecentReview) {
        return;
      } else if (isRecentReview) {
      }

      if (review.status_transitions && Array.isArray(review.status_transitions)) {
        // Find the most recent verification attempt by looking for the last "verifying" transition
        let lastVerifyingIndex = -1;
        for (let i = review.status_transitions.length - 1; i >= 0; i--) {
          if (review.status_transitions[i].to_status === 'verifying') {
            lastVerifyingIndex = i;
            break;
          }
        }

        // Only look at transitions after the most recent verification attempt
        const relevantTransitions = lastVerifyingIndex >= 0
          ? review.status_transitions.slice(lastVerifyingIndex)
          : review.status_transitions;

        // Check if the most recent verification was successful
        const hasSuccessfulValidation = relevantTransitions.some(transition => {
          const comments = transition.transition_comments || '';
          return comments.includes('Document verification completed successfully') ||
                 comments.includes('all employee data validated') ||
                 comments.includes('completed successfully') ||
                 comments.includes('validation successful');
        });

        // If validation was successful, don't extract any errors
        if (hasSuccessfulValidation) {
          return;
        }

        // Otherwise, look for error transitions in the most recent verification attempt
        const errorTransitions = relevantTransitions.filter(transition => {
          const comments = transition.transition_comments || '';
          return comments.includes('Missing required fields') ||
                 comments.includes('validation failed') ||
                 comments.includes('N8N processing failed') ||
                 comments.includes('Employee validation warning') ||
                 comments.includes('not found') ||
                 comments.includes('cannot be empty') ||
                 comments.includes('Multiple') ||
                 comments.includes('Error updating employee');
        });

        // Only process the most recent error transition to avoid stale errors
        const recentErrorTransition = errorTransitions[errorTransitions.length - 1];
        if (recentErrorTransition) {
          const comments = recentErrorTransition.transition_comments || '';

            // Apply error mappings
            errorMappings.forEach(mapping => {
              if (mapping.pattern.test(comments)) {
                mapping.fields.forEach(field => errors.add(field));
              }
            });

            // Handle missing required fields lists
            const missingFieldsMatch = comments.match(/Missing required fields: ([^.]+)/);
            if (missingFieldsMatch) {
              missingFieldsMatch[1].split(',').forEach(field => {
                errors.add(field.trim());
              });
            }

          // Handle employee-specific field errors
          const employeeFieldMatches = [...comments.matchAll(/employees\[(\d+)\]\.(\w+)/g)];
          employeeFieldMatches.forEach(match => {
            errors.add(match[2]); // Add the field name (firstName, lastName, jobTitle, etc.)
          });
        }
      }

      // Also check the main review comments for validation errors, but only if it's the most recent error
      if (isErrorState && review.comments && index === sortedReviews.length - 1) {
        const comments = review.comments;

        // Apply the same error mappings to the main review comments
        errorMappings.forEach(mapping => {
          if (mapping.pattern.test(comments)) {
            mapping.fields.forEach(field => {
              errors.add(field);
            });
          }
        });

        // Handle employee-specific field errors in main comments
        const employeeFieldMatches = [...comments.matchAll(/employees\[(\d+)\]\.(\w+)/g)];
        employeeFieldMatches.forEach(match => {
          errors.add(match[2]); // Add the field name (firstName, lastName, jobTitle, etc.)
        });
      }
    });

    // Debug: log detected errors
    if (errors.size > 0) {
    } else {
    }

    return errors;
  };

  // Synchronous validation for immediate feedback (client-side only)
  const validateFieldSync = (fieldName, value, employeeIndex = null) => {
    // Skip validation for empty values during typing (only validate on blur or when complete)
    if (!value || typeof value !== 'string') return false;

    const trimmedValue = value.trim();

    switch (fieldName) {
      case 'contractorName':
      case 'subcontractorName':
      case 'projectName':
        // Contractor/project validation uses historical errors + real-time clearing only
        // No client-side validation - rely on server validation and real-time correction
        return false;

      case 'firstName':
      case 'lastName':
        return trimmedValue === '' ||
               trimmedValue.length < 1 ||
               !/^[a-zA-Z\s\-'.]+$/.test(trimmedValue); // Basic name validation

      case 'jobTitle':
        return trimmedValue === '' || trimmedValue.length < 2;

      case 'forWeekEnding':
      case 'payrollStartDate':
      case 'payrollEndDate':
      case 'dateSigned':
        // Basic date validation
        if (trimmedValue === '') return true; // Empty is an error
        if (trimmedValue === '05/22/2024' || trimmedValue === '05/16/2024' ||
            trimmedValue === '05/21/2024' || trimmedValue === '05/31/2024') return false; // Default test values are valid
        // Simple date format check
        return !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmedValue);

      default:
        return false;
    }
  };

  // Employee validation now uses same real-time pattern as contractor validation

  // Helper function to check if a field has validation errors - SIMPLIFIED APPROACH
  const hasValidationError = (fieldName, employeeIndex = null) => {

    // Check proactive validation errors first (multiple matches detected)
    if (proactiveValidationErrors.has(fieldName)) {
      return true;
    }

    // Get the most recent review comments to check for validation errors
    if (!documentReviews || documentReviews.length === 0) {
      return false;
    }

    const mostRecentReview = documentReviews[0];
    const comments = mostRecentReview.comments || '';


    // Use specific error patterns for each field type to avoid false positives

    if (fieldName === 'contractorName' || fieldName === 'subcontractorName') {
      // Check for specific contractor validation error patterns
      const contractorErrorPatterns = [
        'contractor.*not found',
        'contractor.*validation.*error',
        'contractor.*error',
        'contractor.*required',
        'contractor.*cannot be empty',
        'subcontractor.*not found',
        'subcontractor.*validation.*error',
        'subcontractor.*error',
        'subcontractor.*required',
        'subcontractor.*cannot be empty',
        'multiple contractors',
        'multiple companies',
        '^contractor.*not found',  // Starts with contractor
        '^subcontractor.*not found'  // Starts with subcontractor
      ];

      const hasContractorError = contractorErrorPatterns.some(pattern => {
        const regex = new RegExp(pattern, 'i');
        return regex.test(comments);
      });

      if (hasContractorError) {
        return true;
      }
    }

    if (fieldName === 'projectName') {
      // Check for specific project validation error patterns
      const projectErrorPatterns = [
        'project not found',
        'project name not found',
        'project.*not found',
        'projectname.*not found',
        'project.*validation',
        'project.*error',
        'project.*required',
        'project.*cannot be empty'
      ];

      const hasProjectError = projectErrorPatterns.some(pattern => {
        const regex = new RegExp(pattern, 'i');
        return regex.test(comments);
      });

      if (hasProjectError) {
        return true;
      }
    }

    if (fieldName === 'jobTitle') {
      // Check for specific job title validation error patterns
      const jobTitleErrorPatterns = [
        'job title.*not found',
        'job title.*validation',
        'job title.*error',
        'job title.*required',
        'job title.*cannot be empty',
        'jobtitle.*not found',
        'jobtitle.*validation',
        'jobtitle.*error'
      ];

      const hasJobTitleError = jobTitleErrorPatterns.some(pattern => {
        const regex = new RegExp(pattern, 'i');
        return regex.test(comments);
      });

      if (hasJobTitleError) {
        return true;
      }
    }

    // For employee fields, check if employee name appears with error keywords
    if (employeeIndex !== null && ['firstName', 'lastName'].includes(fieldName)) {
      const employee = metadataFormData.employees?.[employeeIndex];
      if (employee) {
        const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();

        // Check for specific employee validation error patterns
        // Only match errors that are specific to THIS employee, not generic employee errors
        const employeeSpecificPatterns = [
          // Match errors mentioning this specific employee's name
          `${fullName}.*not found`,
          `${fullName}.*validation error`,
          `${fullName}.*multiple.*found`,
          // Match errors for empty names only if this employee's name is actually empty
          ...((!employee.firstName || employee.firstName.trim() === '') && fieldName === 'firstName' ? ['Employee first name cannot be empty'] : []),
          ...((!employee.lastName || employee.lastName.trim() === '') && fieldName === 'lastName' ? ['Employee last name cannot be empty'] : [])
        ];

        // Also check for generic patterns ONLY if they appear with this employee's context
        // Look for patterns like "Employee 1: not found" or "Row 2: Employee not found"
        const employeeIndexPatterns = [
          `Employee ${employeeIndex + 1}:.*not found`,
          `Row ${employeeIndex + 1}:.*Employee.*not found`,
          `employees\\[${employeeIndex}\\]\\.firstName`,
          `employees\\[${employeeIndex}\\]\\.lastName`
        ];

        const allPatterns = [...employeeSpecificPatterns, ...employeeIndexPatterns];
        
        const hasEmployeeError = allPatterns.some(pattern => {
          const regex = new RegExp(pattern, 'i');
          return regex.test(comments);
        });

        if (hasEmployeeError) {
          return true;
        }
      }
    }

    // For date fields, check for specific date validation error patterns
    if (['forWeekEnding', 'payrollStartDate', 'payrollEndDate', 'dateSigned'].includes(fieldName)) {
      const dateErrorPatterns = [
        `${fieldName}.*not found`,
        `${fieldName}.*validation`,
        `${fieldName}.*error`,
        `${fieldName}.*required`,
        `${fieldName}.*cannot be empty`,
        `${fieldName}.*invalid`,
        'date.*validation',
        'date.*error',
        'week ending.*error',
        'payroll.*date.*error',
        'signed.*date.*error'
      ];

      const hasDateError = dateErrorPatterns.some(pattern => {
        const regex = new RegExp(pattern, 'i');
        return regex.test(comments);
      });

      if (hasDateError) {
        return true;
      }
    }

    return false;
  };

  // Function to apply matching rules to metadata when loading
  const applyMatchingRulesToMetadata = async (fieldData) => {
    const fieldsToCheck = ['contractorName', 'subcontractorName', 'projectName'];
    const updatedFieldData = { ...fieldData };
    const appliedRules = {};
    let hasChanges = false;

    try {
      // Make a single API call to get all matching rules instead of 3 separate calls
      const response = await axios.get('/matching-rules?active_only=true', {
        headers: getAuthHeaders()
      });

      if (response.data && response.data.length > 0) {
        const allRules = response.data;

        // Check each field against all rules
        for (const fieldName of fieldsToCheck) {
          const currentValue = fieldData[fieldName];
          if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
            // Find exact match for the input pattern and field
            const matchingRule = allRules.find(rule =>
              rule.field_name === fieldName &&
              rule.input_pattern === currentValue &&
              rule.is_active
            );

            if (matchingRule) {
              updatedFieldData[fieldName] = matchingRule.mapped_value;
              appliedRules[fieldName] = {
                originalValue: currentValue,
                mappedValue: matchingRule.mapped_value,
                ruleId: matchingRule.id,
                createdDate: matchingRule.created_at,
                createdBy: matchingRule.created_by
              };
              hasChanges = true;
              console.log(`📋 Applied rule for ${fieldName}: "${currentValue}" → "${matchingRule.mapped_value}"`);
            }
          }
        }

        // Check job titles in employees array
        if (updatedFieldData.employees && Array.isArray(updatedFieldData.employees)) {
          for (let i = 0; i < updatedFieldData.employees.length; i++) {
            const employee = updatedFieldData.employees[i];
            if (employee.jobTitle && typeof employee.jobTitle === 'string' && employee.jobTitle.trim()) {
              // Find exact match for job title
              const matchingRule = allRules.find(rule =>
                rule.field_name === 'jobTitle' &&
                rule.input_pattern === employee.jobTitle &&
                rule.is_active
              );

              if (matchingRule) {
                updatedFieldData.employees[i] = {
                  ...employee,
                  jobTitle: matchingRule.mapped_value
                };
                
                // Store per-employee rule info
                if (!appliedRules.jobTitle) {
                  appliedRules.jobTitle = {};
                }
                appliedRules.jobTitle[i] = {
                  originalValue: employee.jobTitle,
                  mappedValue: matchingRule.mapped_value,
                  ruleId: matchingRule.id,
                  createdDate: matchingRule.created_at,
                  createdBy: matchingRule.created_by
                };
                hasChanges = true;
                console.log(`📋 Applied rule for employee ${i} jobTitle: "${employee.jobTitle}" → "${matchingRule.mapped_value}"`);
              }
            }
          }
        }
      }
    } catch (error) {
      // Handle case where matching_rules table doesn't exist gracefully
      // Continue without rule mapping
      console.warn('⚠️ Could not apply matching rules:', error.message);
    }

    // Update rule replacements state for UI notifications
    setRuleReplacements(prev => ({
      ...prev,
      ...appliedRules
    }));

    return updatedFieldData;
  };

  // Functions to handle suggestions and matching rules
  const fetchSuggestions = useCallback(async (fieldName, inputValue, employeeIndex = null) => {
    try {
      const fieldKey = fieldName === 'jobTitle' && employeeIndex !== null ? `${fieldName}:${employeeIndex}` : fieldName;
      console.log(`🔍 Fetching suggestions for ${fieldKey}: ${inputValue}`);
      
      let suggestions = [];
      const originalValue = inputValue;
      
      // Progressive search: try original term, then progressively shorter terms
      const trySearch = async (searchTerm) => {
        let response;
        
        if (fieldName === 'contractorName' || fieldName === 'subcontractorName') {
          // Call contractors endpoint
          response = await axios.get(`/contractors?contractor_name=${encodeURIComponent(searchTerm)}`, {
            headers: getAuthHeaders()
          });
          
          // Extract company names from contractor objects
          if (response.data && Array.isArray(response.data)) {
            return response.data.map(contractor => contractor.company).filter(Boolean);
          }
        } else if (fieldName === 'projectName') {
          // Call contracts endpoint  
          response = await axios.get(`/contracts?contract_name=${encodeURIComponent(searchTerm)}`, {
            headers: getAuthHeaders()
          });
          
          // Extract contract descriptions from contract objects
          if (response.data && Array.isArray(response.data)) {
            return response.data.map(contract => contract.contract_descr).filter(Boolean);
          }
        } else if (fieldName === 'jobTitle') {
          // Call titles endpoint
          response = await axios.get(`/titles?title_name=${encodeURIComponent(searchTerm)}`, {
            headers: getAuthHeaders()
          });
          
          // Extract title names from title objects
          if (response.data && Array.isArray(response.data)) {
            return response.data.map(title => title.title).filter(Boolean);
          }
        }
        
        return [];
      };

      // Start with the original input value
      let currentSearchTerm = inputValue.trim();
      let attemptCount = 0;
      const maxAttempts = 25; // Increased limit for longer project names

      // Progressive search loop - try different strategies
      while (suggestions.length === 0 && currentSearchTerm.length >= 3 && attemptCount < maxAttempts) {
        console.log(`🔍 Attempt ${attemptCount + 1}: Searching for "${currentSearchTerm}"`);
        
        suggestions = await trySearch(currentSearchTerm);
        
        if (suggestions.length === 0 && currentSearchTerm.length > 3) {
          attemptCount++;
          
          // For very long terms, use intelligent truncation
          if (currentSearchTerm.length > 20) {
            // Try removing chunks instead of single characters for efficiency
            if (attemptCount % 3 === 0) {
              // Every 3rd attempt, remove a larger chunk (5 characters)
              currentSearchTerm = currentSearchTerm.slice(0, -5);
            } else {
              // Otherwise remove 2 characters at a time
              currentSearchTerm = currentSearchTerm.slice(0, -2);
            }
          } else {
            // For shorter terms, remove one character at a time
            currentSearchTerm = currentSearchTerm.slice(0, -1);
          }
          
          // Ensure we don't go below minimum length
          if (currentSearchTerm.length < 3) {
            break;
          }
        } else {
          break;
        }
      }

      console.log(`📋 Found ${suggestions.length} suggestions after ${attemptCount + 1} attempts:`, suggestions);

      if (suggestions && suggestions.length > 0) {
        setSuggestions(prev => {
          if (fieldName === 'jobTitle' && employeeIndex !== null) {
            return {
              ...prev,
              jobTitle: {
                ...prev.jobTitle,
                [employeeIndex]: {
                  visible: true,
                  items: suggestions,
                  originalValue: originalValue
                }
              }
            };
          } else {
            return {
              ...prev,
              [fieldName]: {
                visible: true,
                items: suggestions,
                originalValue: originalValue
              }
            };
          }
        });
        
        // Log if we had to truncate the search term
        if (currentSearchTerm !== originalValue) {
          console.log(`✂️ Found matches by truncating "${originalValue}" to "${currentSearchTerm}"`);
        }
      } else {
        console.log(`❌ No suggestions found for ${fieldKey}: ${originalValue} (tried down to "${currentSearchTerm}")`);
      }
    } catch (error) {
      console.error('📡 Error fetching suggestions:', error);
      console.error('📡 Error details:', error.response?.data);
    }
  }, [getAuthHeaders]);

  // Function to proactively check for multiple matches and trigger validation errors
  const checkForMultipleMatches = useCallback(async (fieldName, inputValue, employeeIndex = null) => {
    if (!inputValue || inputValue.trim().length < 2) {
      // Clear any existing proactive validation error for this field
      setProactiveValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(fieldName);
        return newErrors;
      });
      return false;
    }
    
    // Check if this field+value combination has already been validated
    const validationKey = `${fieldName}:${inputValue.trim()}`;
    if (validatedFields.current.has(validationKey)) {
      console.log(`⏭️ Skipping validation for ${fieldName} - already validated: "${inputValue}"`);
      return false;
    }
    
    try {
      console.log(`🔍 Checking for multiple matches: ${fieldName} = "${inputValue}"`);
      
      let matches = [];
      const originalValue = inputValue;
      
      // Progressive search: try original term, then progressively shorter terms
      const trySearch = async (searchTerm) => {
        let response;
        
        if (fieldName === 'contractorName' || fieldName === 'subcontractorName') {
          response = await axios.get(`/contractors?contractor_name=${encodeURIComponent(searchTerm)}`, {
            headers: getAuthHeaders()
          });
          return response.data?.map(contractor => contractor.company).filter(Boolean) || [];
        } else if (fieldName === 'projectName') {
          response = await axios.get(`/contracts?contract_name=${encodeURIComponent(searchTerm)}`, {
            headers: getAuthHeaders()
          });
          return response.data?.map(contract => contract.contract_descr).filter(Boolean) || [];
        } else if (fieldName === 'jobTitle') {
          response = await axios.get(`/titles?title_name=${encodeURIComponent(searchTerm)}`, {
            headers: getAuthHeaders()
          });
          return response.data?.map(title => title.title).filter(Boolean) || [];
        }
        
        return [];
      };

      // Start with the original input value
      let currentSearchTerm = inputValue.trim();
      let attemptCount = 0;
      const maxAttempts = 25; // Increased limit for longer project names

      // Progressive search loop - try different strategies
      while (matches.length === 0 && currentSearchTerm.length >= 3 && attemptCount < maxAttempts) {
        console.log(`🔍 Attempt ${attemptCount + 1}: Searching for "${currentSearchTerm}"`);
        
        matches = await trySearch(currentSearchTerm);
        
        if (matches.length === 0 && currentSearchTerm.length > 3) {
          attemptCount++;
          
          // For very long terms, use intelligent truncation
          if (currentSearchTerm.length > 20) {
            // Try removing chunks instead of single characters for efficiency
            if (attemptCount % 3 === 0) {
              // Every 3rd attempt, remove a larger chunk (5 characters)
              currentSearchTerm = currentSearchTerm.slice(0, -5);
            } else {
              // Otherwise remove 2 characters at a time
              currentSearchTerm = currentSearchTerm.slice(0, -2);
            }
          } else {
            // For shorter terms, remove one character at a time
            currentSearchTerm = currentSearchTerm.slice(0, -1);
          }
          
          // Ensure we don't go below minimum length
          if (currentSearchTerm.length < 3) {
            break;
          }
        } else {
          break;
        }
      }
      
      console.log(`📋 Found ${matches.length} matches for ${fieldName} after ${attemptCount + 1} attempts:`, matches);
      
      // Log if we had to truncate the search term
      if (currentSearchTerm !== originalValue && matches.length > 0) {
        console.log(`✂️ Found matches by truncating "${originalValue}" to "${currentSearchTerm}"`);
      }
      
      // Check if we need to show suggestions
      const needsSuggestions = matches.length > 1 || 
        (matches.length === 1 && matches[0].toLowerCase() !== originalValue.toLowerCase());
      
      if (needsSuggestions) {
        if (matches.length > 1) {
          console.log(`⚠️ Multiple matches detected for ${fieldName}, showing suggestions`);
        } else {
          console.log(`⚠️ Non-exact match detected for ${fieldName}: "${originalValue}" → "${matches[0]}", showing suggestions`);
        }
        
        // Show suggestions dropdown
        setSuggestions(prev => {
          if (fieldName === 'jobTitle' && employeeIndex !== null) {
            return {
              ...prev,
              jobTitle: {
                ...prev.jobTitle,
                [employeeIndex]: {
                  visible: true,
                  items: matches,
                  originalValue: originalValue
                }
              }
            };
          } else {
            return {
              ...prev,
              [fieldName]: {
                visible: true,
                items: matches,
                originalValue: originalValue
              }
            };
          }
        });
        
        // Add to proactive validation errors (will highlight field in red)
        setProactiveValidationErrors(prev => new Set([...prev, fieldName]));
        
        return true; // Has validation issue = needs user selection
      } else {
        console.log(`✅ Exact match for ${fieldName}, clearing validation error`);
        
        // Mark this field as validated to prevent re-validation
        validatedFields.current.set(`${fieldName}:${originalValue}`, true);
        
        // Clear any existing validation error for this field
        setProactiveValidationErrors(prev => {
          const newErrors = new Set(prev);
          newErrors.delete(fieldName);
          return newErrors;
        });
        
        // Hide suggestions if exact match
        setSuggestions(prev => {
          if (fieldName === 'jobTitle' && employeeIndex !== null) {
            return {
              ...prev,
              jobTitle: {
                ...prev.jobTitle,
                [employeeIndex]: { visible: false, items: [], originalValue: '' }
              }
            };
          } else {
            return {
              ...prev,
              [fieldName]: { visible: false, items: [], originalValue: '' }
            };
          }
        });
        
        // Clear the processing state for this field
        suggestionsBeingProcessed.current.delete(fieldName);
        
        return false;
      }
    } catch (error) {
      console.error(`❌ Error checking for multiple matches for ${fieldName}:`, error);
      return false;
    }
  }, [getAuthHeaders]);

  // Master debounced wrapper for all suggestion operations to prevent rapid toggles
  const debouncedSuggestionOperation = useCallback((operation, fieldName, inputValue, employeeIndex = null) => {
    const operationKey = `${operation}_${fieldName}${employeeIndex !== null ? `:${employeeIndex}` : ''}`;
    
    // Clear any existing timeout for this field+operation
    if (suggestionTimeouts.current[operationKey]) {
      clearTimeout(suggestionTimeouts.current[operationKey]);
    }

    // Set a new timeout
    suggestionTimeouts.current[operationKey] = setTimeout(async () => {
      // Check if this field is already being processed
      const processingKey = employeeIndex !== null ? `${fieldName}:${employeeIndex}` : fieldName;
      if (suggestionsBeingProcessed.current.has(processingKey)) {
        console.log(`⏭️ Skipping ${operation} for ${processingKey} - already being processed`);
        return;
      }

      // Mark as being processed
      suggestionsBeingProcessed.current.add(processingKey);

      try {
        if (operation === 'fetchSuggestions') {
          await fetchSuggestions(fieldName, inputValue, employeeIndex);
        } else if (operation === 'checkForMultipleMatches') {
          await checkForMultipleMatches(fieldName, inputValue, employeeIndex);
        }
      } finally {
        // Always remove from processing set
        suggestionsBeingProcessed.current.delete(processingKey);
      }
    }, suggestionDebounceDelay);
  }, [fetchSuggestions, checkForMultipleMatches, suggestionDebounceDelay]);

  // Legacy wrapper for backward compatibility
  const debouncedFetchSuggestions = useCallback((fieldName, inputValue) => {
    debouncedSuggestionOperation('fetchSuggestions', fieldName, inputValue);
  }, [debouncedSuggestionOperation]);

  // New debounced wrapper for checkForMultipleMatches
  const debouncedCheckForMultipleMatches = useCallback((fieldName, inputValue, employeeIndex = null) => {
    debouncedSuggestionOperation('checkForMultipleMatches', fieldName, inputValue, employeeIndex);
  }, [debouncedSuggestionOperation]);

  const handleSuggestionSelect = (fieldName, selectedValue, employeeIndex = 0) => {
    // Handle job title selections differently since they're part of employee data
    if (fieldName === 'jobTitle') {
      // Update the specific employee's job title using the provided index
      if (metadataFormData.employees && metadataFormData.employees.length > employeeIndex) {
        const updatedEmployees = [...metadataFormData.employees];
        updatedEmployees[employeeIndex] = { ...updatedEmployees[employeeIndex], jobTitle: selectedValue };
        handleMetadataChange('employees', updatedEmployees);
      }
    } else {
      // Handle contractor/project selections normally
      handleMetadataChange(fieldName, selectedValue);
    }

    // Clear any proactive validation error for this field
    setProactiveValidationErrors(prev => {
      const newErrors = new Set(prev);
      newErrors.delete(fieldName);
      return newErrors;
    });

    // Hide the suggestions dropdown
    setSuggestions(prev => {
      if (fieldName === 'jobTitle') {
        return {
          ...prev,
          jobTitle: {
            ...prev.jobTitle,
            [employeeIndex]: { visible: false, items: [], originalValue: '' }
          }
        };
      } else {
        return {
          ...prev,
          [fieldName]: { visible: false, items: [], originalValue: '' }
        };
      }
    });
    
    // Clear the processing state for this field
    suggestionsBeingProcessed.current.delete(fieldName);
  };

  const handleCreateMatchingRule = async (ruleData) => {
    try {
      const response = await axios.post('/create-rule-from-selection', {
        field_name: ruleData.fieldName,
        original_input: ruleData.originalValue,
        selected_value: ruleData.selectedValue,
        rule_type: 'exact_match'
      }, {
        headers: getAuthHeaders()
      });

      // Track the rule replacement for user notification
      setRuleReplacements(prev => {
        if (ruleData.fieldName === 'jobTitle' && ruleData.employeeIndex !== undefined) {
          return {
            ...prev,
            jobTitle: {
              ...prev.jobTitle,
              [ruleData.employeeIndex]: {
                originalValue: ruleData.originalValue,
                mappedValue: ruleData.selectedValue,
                ruleId: response.data?.id || 'new',
                createdDate: new Date().toISOString(),
                createdBy: 'current_user',
                justCreated: true
              }
            }
          };
        } else {
          return {
            ...prev,
            [ruleData.fieldName]: {
              originalValue: ruleData.originalValue,
              mappedValue: ruleData.selectedValue,
              ruleId: response.data?.id || 'new',
              createdDate: new Date().toISOString(),
              createdBy: 'current_user',
              justCreated: true
            }
          };
        }
      });

      console.log(`📋 Created new rule for ${ruleData.fieldName}: "${ruleData.originalValue}" → "${ruleData.selectedValue}"`);

      // Update the form field with the selected value
      if (ruleData.fieldName === 'jobTitle') {
        // Mark as programmatic update to prevent clearing rule notification
        programmaticUpdates.current.add('jobTitle');
        
        // For job titles, update the specific employee's job title
        const employeeIndex = ruleData.employeeIndex || 0;
        if (metadataFormData.employees && metadataFormData.employees.length > employeeIndex) {
          const updatedEmployees = [...metadataFormData.employees];
          updatedEmployees[employeeIndex] = { ...updatedEmployees[employeeIndex], jobTitle: ruleData.selectedValue };
          handleMetadataChange('employees', updatedEmployees);
        }
        
        // Clear the programmatic update flag after a brief delay
        setTimeout(() => {
          programmaticUpdates.current.delete('jobTitle');
        }, 100);
      } else {
        // Mark as programmatic update to prevent clearing rule notification
        programmaticUpdates.current.add(ruleData.fieldName);
        handleMetadataChange(ruleData.fieldName, ruleData.selectedValue);
        
        // Clear the programmatic update flag after a brief delay
        setTimeout(() => {
          programmaticUpdates.current.delete(ruleData.fieldName);
        }, 100);
      }

      // Clear any proactive validation error for this field
      setProactiveValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(ruleData.fieldName);
        return newErrors;
      });

      // Hide the suggestions dropdown
      setSuggestions(prev => ({
        ...prev,
        [ruleData.fieldName]: { visible: false, items: [], originalValue: '' }
      }));
      
      // Clear the processing state for this field
      suggestionsBeingProcessed.current.delete(ruleData.fieldName);

    } catch (error) {
      console.error('Error creating matching rule:', error);
      // Still apply the selection even if rule creation fails
      handleSuggestionSelect(ruleData.fieldName, ruleData.selectedValue);
    }
  };

  // Function to clear rule replacement notifications when user manually edits field
  const clearRuleNotification = (fieldName, employeeIndex = null) => {
    setRuleReplacements(prev => {
      if (fieldName === 'jobTitle' && employeeIndex !== null) {
        return {
          ...prev,
          jobTitle: {
            ...prev.jobTitle,
            [employeeIndex]: null
          }
        };
      } else {
        return {
          ...prev,
          [fieldName]: null
        };
      }
    });
  };

  // Rule notification component
  const RuleNotification = ({ fieldName, ruleInfo }) => {
    if (!ruleInfo) return null;

    const formatDate = (dateString) => {
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return '';
      }
    };

    const icon = ruleInfo.justCreated ? '✅' : '🔄';
    const message = ruleInfo.justCreated 
      ? 'New rule created and applied'
      : 'Existing rule applied automatically';

    // Auto-dismiss "just created" notifications after 5 seconds
    useEffect(() => {
      if (ruleInfo.justCreated) {
        const timer = setTimeout(() => {
          clearRuleNotification(fieldName);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [ruleInfo.justCreated, fieldName]);

    return (
      <div className={`rule-notification ${ruleInfo.justCreated ? 'just-created' : ''}`}>
        <span className="rule-notification-icon">{icon}</span>
        <div className="rule-notification-content">
          <div><strong>{message}:</strong></div>
          <div>
            <span className="rule-notification-original">"{ruleInfo.originalValue}"</span>
            {' → '}
            <span className="rule-notification-mapped">"{ruleInfo.mappedValue}"</span>
          </div>
          {!ruleInfo.justCreated && ruleInfo.createdDate && (
            <div style={{ fontSize: '11px', marginTop: '2px', opacity: 0.8 }}>
              Rule created {formatDate(ruleInfo.createdDate)}
            </div>
          )}
        </div>
        <button 
          className="rule-notification-dismiss"
          onClick={() => clearRuleNotification(fieldName)}
          title="Dismiss notification"
        >
          ×
        </button>
      </div>
    );
  };

  // Check validation errors for suggestion opportunities
  const checkForSuggestionOpportunities = useCallback(() => {
    if (!documentReviews || documentReviews.length === 0 || !metadataFormData) {
      return;
    }

    const mostRecentReview = documentReviews[0];
    const comments = mostRecentReview.comments || '';

    // Enhanced patterns for contractor multiple matches
    const contractorMultiplePatterns = [
      'Multiple contractors found',
      'multiple contractor matches',
      'Found these contractors:'
    ];

    const projectMultiplePatterns = [
      'CONFLICT_MULTIPLE_CONTRACTS:',
      'Multiple projects found',
      'multiple project matches',
      'Found these contracts:'
    ];

    // Helper function to extract original input value from error messages
    const extractOriginalValue = (comments, fieldType) => {
      // Try to extract the original input from error messages
      let patterns = [];
      if (fieldType === 'contractor') {
        patterns = [
          /Multiple contractors found.*searching for ['"]([^'"]+)['"]/i,
          /contractor ['"]([^'"]+)['"].*multiple matches/i,
          /searching for contractor ['"]([^'"]+)['"]/i
        ];
      } else if (fieldType === 'project') {
        patterns = [
          /CONFLICT_MULTIPLE_CONTRACTS:.*['"]([^'"]+)['"]/i,
          /project ['"]([^'"]+)['"].*multiple matches/i,
          /searching for project ['"]([^'"]+)['"]/i
        ];
      }

      for (const pattern of patterns) {
        const match = comments.match(pattern);
        if (match && match[1]) {
          return match[1];
        }
      }
      return null;
    };

    // Check for contractor multiple matches
    const hasContractorMultiple = contractorMultiplePatterns.some(pattern => comments.includes(pattern));
    if (hasContractorMultiple && metadataFormData.contractorName && !suggestions.contractorName.visible) {
      const originalInput = extractOriginalValue(comments, 'contractor') || metadataFormData.contractorName;
      debouncedFetchSuggestions('contractorName', originalInput);
    }
    if (hasContractorMultiple && metadataFormData.subcontractorName && !suggestions.subcontractorName.visible) {
      const originalInput = extractOriginalValue(comments, 'contractor') || metadataFormData.subcontractorName;
      debouncedFetchSuggestions('subcontractorName', originalInput);
    }

    // Check for project multiple matches
    const hasProjectMultiple = projectMultiplePatterns.some(pattern => comments.includes(pattern));
    if (hasProjectMultiple && metadataFormData.projectName && !suggestions.projectName.visible) {
      const originalInput = extractOriginalValue(comments, 'project') || metadataFormData.projectName;
      debouncedFetchSuggestions('projectName', originalInput);
    }

  }, [documentReviews, metadataFormData, suggestions, debouncedFetchSuggestions]);

  // Function to load document preview
  const loadDocumentPreview = async (docId) => {
    if (!docId) {
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const authHeaders = getAuthHeaders();

      // Create a blob URL for the document preview
      const response = await axios.get(`/documents/${docId}/preview`, {
        headers: authHeaders,
        responseType: 'blob'
      });


      if (response.status === 200) {
        const blob = response.data;
        // Ensure the blob has the correct MIME type for PDF display
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const previewUrl = URL.createObjectURL(pdfBlob);
        setDocumentPreviewUrl(previewUrl);
      } else {
        console.warn('loadDocumentPreview: Unexpected response status', response.status);
        setPreviewError('Unable to load document preview. Unexpected response status.');
      }
    } catch (err) {
      console.error('loadDocumentPreview: Error loading document preview:', err);
      console.error('loadDocumentPreview: Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      setPreviewError('Unable to load document preview. The file may not be available.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleanup function for preview URL
  const cleanupPreviewUrl = () => {
    if (documentPreviewUrl) {
      URL.revokeObjectURL(documentPreviewUrl);
      setDocumentPreviewUrl(null);
    }
  };

  useEffect(() => {
    const fetchDocumentAndMetadata = async () => {
      try {
        // Fetch document details
        const docResponse = await axios.get(`/documents/${documentId}`, {
          headers: getAuthHeaders()
        });

        // Fetch document metadata
        const metaResponse = await axios.get(`/documents/${documentId}/metadata`, {
          headers: getAuthHeaders()
        });

        // Fetch document reviews
        const reviewsResponse = await axios.get(`/documents/${documentId}/reviews`, {
          headers: getAuthHeaders()
        });

        if (docResponse.status === 200) {
          const doc = docResponse.data;
          setDocument(doc);
          setFormData({
            title: doc.title || '',
            status: doc.status || '',
            form_type: doc.form_type || '',
            uploader_id: doc.uploader_id || '',
            external_reference_id: doc.external_reference_id || ''
          });
        }

        if (metaResponse.status === 200) {
          setMetadata(metaResponse.data);
          // Initialize metadata form data with current values
          if (metaResponse.data.length > 0) {
            const latestMetadata = metaResponse.data[0];
            const fieldData = latestMetadata.field_data || {};

            // Convert daysWorked array to legacy daily hour fields for WH-347 forms
            if (fieldData.employees) {
              fieldData.employees = convertDaysWorkedToLegacyFormat(
                fieldData.employees,
                fieldData.payrollStartDate,
                fieldData.payrollEndDate
              );
            }

            // Apply matching rules to automatically map contractor and project names
            const updatedFieldData = await applyMatchingRulesToMetadata(fieldData);
            setMetadataFormData(updatedFieldData);

            // Mark that rules have been applied for this document
            rulesAppliedForDocument.current = documentId;

            // Store original values before any rule mapping for manual rule creation
            setOriginalFieldValues({
              contractorName: fieldData.contractorName || '',
              subcontractorName: fieldData.subcontractorName || '',
              projectName: fieldData.projectName || ''
            });
          } else {
            // Initialize empty form data for new documents
            setMetadataFormData({
              companyType: 'contractor',
              contractorName: '',
              subcontractorName: '',
              projectName: '',
              contractNo: '',
              payrollNo: '',
              forWeekEnding: '',
              payrollStartDate: '',
              payrollEndDate: '',
              employees: [],
              dateSigned: '',
              signatoryName: '',
              signatoryTitle: '',
              fringeCheckbox_a: 'unchecked',
              friendCheckbox_b: 'unchecked',
              exceptions: [],
              remarks: ''
            });
          }
        }

        if (reviewsResponse.status === 200) {
          const reviews = reviewsResponse.data;
          setDocumentReviews(reviews);

        }

        setLoading(false);
      } catch (err) {
        setError(formatErrorMessage(err, 'Error fetching document'));
        setLoading(false);
      }
    };

    if (documentId) {
      // Reset auto-calculation prompt state for new editing session
      setHasBeenPromptedForCalculation(false);
      setAutoCalculateEnabled(false);
      setShowCalculationPrompt(false);
      setCalculationContext(null);

      // Reset preview load tracking for new document
      previewLoadAttempted.current = false;

      // Reset rules tracking for new document
      rulesAppliedForDocument.current = null;

      fetchDocumentAndMetadata();
    }
  }, [documentId]);

  // Monitor validation state changes and force re-evaluation
  useEffect(() => {

    // Clear validation cache to force fresh evaluation
    setValidationCache(new Map());

    // Clear validation cache to force fresh evaluation when reviews change
    if (documentReviews.length > 0) {
      const mostRecent = documentReviews[0];
      // Validation cache is cleared above
    }
  }, [documentReviews, validationTrigger]);

  // Check for suggestion opportunities when document reviews change
  useEffect(() => {
    if (documentReviews.length > 0) {
      // Check suggestions whenever reviews are updated (including after corrections)
      checkForSuggestionOpportunities();
    }
  }, [documentReviews, checkForSuggestionOpportunities]); // Include callback in dependencies

  // Run proactive validation when metadata loads to check for multiple matches
  useEffect(() => {
    const runProactiveValidation = async () => {
      if (!metadataFormData || loading) return;
      
      // Skip if suggestions are already being shown from error messages
      const hasActiveSuggestions = Object.values(suggestions).some(s => s.visible);
      if (hasActiveSuggestions) {
        console.log('⏭️ Skipping proactive validation - suggestions already active');
        return;
      }
      
      // Check each field that could have multiple matches
      const fieldsToCheck = [
        { name: 'contractorName', value: metadataFormData.contractorName },
        { name: 'subcontractorName', value: metadataFormData.subcontractorName },
        { name: 'projectName', value: metadataFormData.projectName }
      ];
      
      for (const field of fieldsToCheck) {
        if (field.value && field.value.trim()) {
          // Skip validation if this field has an active rule notification
          if (ruleReplacements[field.name]) {
            console.log(`⏭️ Skipping proactive validation for ${field.name} - rule notification active`);
            continue;
          }
          console.log(`🔍 Running initial proactive validation for ${field.name}: "${field.value}"`);
          debouncedCheckForMultipleMatches(field.name, field.value);
        }
      }
      
      // Also check job titles in employee data
      if (metadataFormData.employees && Array.isArray(metadataFormData.employees)) {
        for (let i = 0; i < metadataFormData.employees.length; i++) {
          const employee = metadataFormData.employees[i];
          if (employee.jobTitle && employee.jobTitle.trim()) {
            // Skip validation if job title has an active rule notification
            if (ruleReplacements.jobTitle[i]) {
              console.log(`⏭️ Skipping proactive validation for employee ${i} jobTitle - rule notification active`);
              continue;
            }
            console.log(`🔍 Running initial proactive validation for employee ${i} jobTitle: "${employee.jobTitle}"`);
            debouncedCheckForMultipleMatches('jobTitle', employee.jobTitle, i);
          }
        }
      }
    };
    
    runProactiveValidation();
  }, [metadataFormData, loading, suggestions, ruleReplacements, debouncedCheckForMultipleMatches]); // Include ruleReplacements to skip validated fields

  // Reset section heights on component mount to prevent excessive heights
  useEffect(() => {
    setSectionHeights({
      documentInfo: 'auto',
      payrollForm: 'auto',
      metadataHistory: 'auto',
      correctionComments: 'auto'
    });
  }, [documentId]); // Reset when document changes

  // Load preview when both auth and document are ready
  useEffect(() => {
    if (!authLoading && !loading && document && !documentPreviewUrl && !previewLoading && !previewError && !previewLoadAttempted.current) {
      previewLoadAttempted.current = true;
      setTimeout(() => {
        loadDocumentPreview(documentId);
      }, 100);
    }
  }, [authLoading, loading, document, documentPreviewUrl, previewLoading, previewError, documentId]);

  // Initialize input widths based on their content
  useEffect(() => {
    if (typeof window === 'undefined' || !window.document) return;

    const adjustAllInputs = () => {
      // Select all numeric inputs that should be dynamically sized
      const selectors = [
        '.legacy-daily-hours',
        '.legacy-ssn-input',
        '.legacy-exemptions-input',
        '.legacy-hours-input',
        '.legacy-rate-input',
        '.legacy-wages-input',
        '.legacy-deduction',
        '.legacy-benefit-input',
        '.legacy-gross-other',
        '.legacy-net-pay'
      ];

      selectors.forEach(selector => {
        const inputs = window.document.querySelectorAll(selector);
        inputs.forEach(input => adjustInputWidth(input));
      });
    };

    // Initial adjustment
    adjustAllInputs();

    // Re-adjust when metadata changes
    const timeoutId = setTimeout(adjustAllInputs, 100);

    return () => clearTimeout(timeoutId);
  }, [metadata, metadataFormData, adjustInputWidth]);

  // Initialize all numeric values as floats with 2 decimal places
  useEffect(() => {
    if (!metadata || metadata.length === 0) return;

    const formatAllNumericValues = () => {
      const updatedFormData = { ...metadataFormData };
      let hasChanges = false;

      // Helper to process numeric fields in employee data
      const processEmployeeNumericFields = (employees) => {
        return employees.map(employee => {
          const updatedEmployee = { ...employee };

          // Daily hours fields
          for (let day = 1; day <= 7; day++) {
            const otKey = `overtimeHours_${day}`;
            const stKey = `standardHours_${day}`;

            if (updatedEmployee[otKey] !== undefined) {
              const formatted = ensureFloatDisplay(updatedEmployee[otKey]);
              if (formatted !== updatedEmployee[otKey]) {
                updatedEmployee[otKey] = formatted;
                hasChanges = true;
              }
            }

            if (updatedEmployee[stKey] !== undefined) {
              const formatted = ensureFloatDisplay(updatedEmployee[stKey]);
              if (formatted !== updatedEmployee[stKey]) {
                updatedEmployee[stKey] = formatted;
                hasChanges = true;
              }
            }
          }

          // Other numeric fields
          const numericFields = [
            'totalOvertimeHoursWorked', 'totalStandardHoursWorked',
            'overtimeRate', 'standardRate', 'overtimeWages', 'standardWages',
            'grossPayThisPeriod', 'netPayAllProjects', 'allProjGross', 'thisProjGross'
          ];

          numericFields.forEach(field => {
            if (updatedEmployee[field] !== undefined) {
              const formatted = ensureFloatDisplay(updatedEmployee[field]);
              if (formatted !== updatedEmployee[field]) {
                updatedEmployee[field] = formatted;
                hasChanges = true;
              }
            }
          });

          // Handle deductions object separately
          if (updatedEmployee.deductions) {
            const deductionFields = ['fica', 'witholdingTax', 'healthInsurance', 'retirement_401k', 'other'];
            deductionFields.forEach(field => {
              if (updatedEmployee.deductions[field] !== undefined) {
                const formatted = ensureFloatDisplay(updatedEmployee.deductions[field]);
                if (formatted !== updatedEmployee.deductions[field]) {
                  updatedEmployee.deductions[field] = formatted;
                  hasChanges = true;
                }
              }
            });
          }

          return updatedEmployee;
        });
      };

      // Process employees in metadataFormData
      if (updatedFormData.employees && Array.isArray(updatedFormData.employees)) {
        updatedFormData.employees = processEmployeeNumericFields(updatedFormData.employees);
      }

      // Update state if there were changes
      if (hasChanges) {
        setMetadataFormData(updatedFormData);
      }
    };

    // Format on mount and when metadata changes
    const timeoutId = setTimeout(formatAllNumericValues, 200);
    return () => clearTimeout(timeoutId);
  }, [metadata, ensureFloatDisplay]);

  // Cleanup effect for preview URL
  useEffect(() => {
    return () => {
      cleanupPreviewUrl();
    };
  }, [documentPreviewUrl]);

  // Cleanup suggestion timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(suggestionTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      suggestionsBeingProcessed.current.clear();
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMetadataChange = async (field, value) => {
    setMetadataFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setMetadataChanged(true); // Mark metadata as changed

    // Clear rule notification when user manually edits field (not programmatic updates)
    if (['contractorName', 'subcontractorName', 'projectName'].includes(field)) {
      if (!programmaticUpdates.current.has(field)) {
        clearRuleNotification(field);
        // Clear validation cache for this field to allow re-validation
        for (const [key] of validatedFields.current) {
          if (key.startsWith(`${field}:`)) {
            validatedFields.current.delete(key);
          }
        }
      }
    }

    // For key validation fields, trigger immediate validation state refresh
    if (['projectName', 'contractorName', 'subcontractorName'].includes(field)) {
      setValidationTrigger(prev => prev + 1);

      // Check for multiple matches and show validation errors/suggestions
      debouncedCheckForMultipleMatches(field, value);

      // Check if this is a manual change that should prompt for rule creation
      checkForManualRuleCreation(field, value);
    }
  };

  // Function to check if user manually changed a field and offer to create a rule
  const checkForManualRuleCreation = (fieldName, newValue) => {
    const originalValue = originalFieldValues[fieldName];

    // Only prompt if:
    // 1. There was an original value (not empty) OR this is a significant new entry
    // 2. The new value is different and not empty
    // 3. No suggestions dropdown is currently visible for this field
    // 4. The change seems intentional (different enough)
    const isSignificantChange = originalValue &&
                               originalValue.trim() &&
                               originalValue !== newValue;

    const isNewSignificantEntry = !originalValue &&
                                 newValue &&
                                 newValue.trim().length > 5; // New entry with meaningful length

    if ((isSignificantChange || isNewSignificantEntry) &&
        newValue &&
        newValue.trim() &&
        !suggestions[fieldName].visible &&
        newValue.length > 2) { // Minimum length to avoid accidental changes

      if (isSignificantChange) {
      } else {
      }

      // Add a small delay to avoid immediate prompts during typing
      setTimeout(() => {
        // Double-check the value hasn't changed again (user is still typing)
        if (metadataFormData[fieldName] === newValue) {
          if (isSignificantChange) {
            promptForManualRuleCreation(fieldName, originalValue, newValue);
          } else {
            // For new entries, check if there might be similar existing values that could be mapped
            checkForSimilarValues(fieldName, newValue);
          }
        }
      }, 1500); // 1.5 second delay
    }
  };

  // Function to check for similar values that could be mapped to the new entry
  const checkForSimilarValues = async (fieldName, newValue) => {
    try {
      console.log(`🔍 Checking for similar values for ${fieldName}: ${newValue}`);
      
      let response;
      let suggestions = [];
      
      // Call the appropriate endpoint based on field name
      if (fieldName === 'contractorName' || fieldName === 'subcontractorName') {
        // Call contractors endpoint
        response = await axios.get(`http://localhost:8001/contractors?contractor_name=${encodeURIComponent(newValue)}`, {
          headers: getAuthHeaders()
        });
        
        // Extract company names from contractor objects
        if (response.data && Array.isArray(response.data)) {
          suggestions = response.data.map(contractor => contractor.company).filter(Boolean);
        }
      } else if (fieldName === 'projectName') {
        // Call contracts endpoint  
        response = await axios.get(`http://localhost:8001/contracts?contract_name=${encodeURIComponent(newValue)}`, {
          headers: getAuthHeaders()
        });
        
        // Extract contract descriptions from contract objects
        if (response.data && Array.isArray(response.data)) {
          suggestions = response.data.map(contract => contract.contract_descr).filter(Boolean);
        }
      }

      if (suggestions && suggestions.length > 0) {
        console.log(`📋 Found ${suggestions.length} similar values:`, suggestions);
        // Found similar values - show them as suggestions
        setSuggestions(prev => ({
          ...prev,
          [fieldName]: {
            visible: true,
            items: suggestions,
            originalValue: newValue
          }
        }));
      }
    } catch (error) {
      // No similar values found - that's fine, user can proceed with their input
      console.log(`ℹ️ No similar values found for ${fieldName}: ${newValue}`);
    }
  };

  // Function to prompt user for manual rule creation
  const promptForManualRuleCreation = (fieldName, originalValue, newValue) => {
    const fieldDisplayName = fieldName === 'projectName' ? 'project' : 'contractor/subcontractor';

    const shouldCreateRule = window.confirm(
      `You changed the ${fieldDisplayName} from "${originalValue}" to "${newValue}".\n\n` +
      `Would you like to create a rule so that "${originalValue}" automatically maps to "${newValue}" in the future?`
    );

    if (shouldCreateRule) {
      handleCreateMatchingRule({
        fieldName,
        originalValue,
        selectedValue: newValue
      });
    }
  };

  // Helper function to handle employee changes
  const handleEmployeeChange = async (employeeIndex, field, value) => {
    const updatedEmployees = [...(metadataFormData.employees || [])];
    if (!updatedEmployees[employeeIndex]) {
      updatedEmployees[employeeIndex] = {};
    }
    updatedEmployees[employeeIndex][field] = value;
    handleMetadataChange('employees', updatedEmployees);

    // For key employee validation fields, trigger immediate validation state refresh
    if (['firstName', 'lastName', 'jobTitle'].includes(field)) {
      setValidationTrigger(prev => prev + 1);
      
      // Check for multiple matches for job titles specifically
      if (field === 'jobTitle') {
        // Only clear rule notification if this is a manual edit (not programmatic)
        if (!programmaticUpdates.current.has('jobTitle')) {
          clearRuleNotification('jobTitle', employeeIndex);
          // Clear validation cache for job title to allow re-validation
          for (const [key] of validatedFields.current) {
            if (key.startsWith('jobTitle:')) {
              validatedFields.current.delete(key);
            }
          }
        }
        debouncedCheckForMultipleMatches('jobTitle', value, employeeIndex);
      }
    }

    // Check if this is a numerical field that should trigger auto-calculation prompt
    // NOTE: Daily hours fields (standardHours_X, overtimeHours_X) are handled by blur event
    // so they won't trigger prompt during typing
    const isDailyHoursField = /^(overtimeHours|standardHours)_[1-7]$/.test(field);

    // Only trigger prompt during typing for non-daily-hours fields
    // Daily hours fields will trigger on blur instead for better UX
    if (!isDailyHoursField && isNumericalField(field) && value !== '' && !isNaN(parseFloat(value)) && !hasBeenPromptedForCalculation) {
      setCalculationContext({
        employeeIndex,
        field,
        value,
        employeeName: `${updatedEmployees[employeeIndex].firstName || ''} ${updatedEmployees[employeeIndex].lastName || ''}`.trim()
      });
      setShowCalculationPrompt(true);
    } else if (!isDailyHoursField && isNumericalField(field) && value !== '' && !isNaN(parseFloat(value)) && autoCalculateEnabled) {
      // If auto-calculate is enabled and user has already been prompted, just apply calculations
      // Skip for daily hours fields as they're handled by blur
      applyCalculatedValues(employeeIndex);
    }
  };

  // Helper function to handle deduction changes (stores in nested deductions structure)
  const handleDeductionChange = (employeeIndex, deductionField, value) => {
    const updatedEmployees = [...(metadataFormData.employees || [])];
    if (!updatedEmployees[employeeIndex]) {
      updatedEmployees[employeeIndex] = {};
    }
    if (!updatedEmployees[employeeIndex].deductions) {
      updatedEmployees[employeeIndex].deductions = {};
    }
    updatedEmployees[employeeIndex].deductions[deductionField] = parseFloat(value) || 0;
    handleMetadataChange('employees', updatedEmployees);

    // Check if this deduction field should trigger auto-calculation prompt or auto-calculation
    const fieldName = `deductions.${deductionField}`;
    if (value !== '' && !isNaN(parseFloat(value))) {
      if (!hasBeenPromptedForCalculation) {
        // First numerical change - show prompt
        setCalculationContext({
          employeeIndex,
          field: fieldName,
          value,
          employeeName: `${updatedEmployees[employeeIndex].firstName || ''} ${updatedEmployees[employeeIndex].lastName || ''}`.trim()
        });
        setShowCalculationPrompt(true);
      } else if (autoCalculateEnabled) {
        // Auto-calculate is enabled - apply calculations automatically
        applyCalculatedValues(employeeIndex);
      }
    }
  };

  // List of numerical fields that should trigger auto-calculation prompt
  const numericalFields = [
    'totalStandardHoursWorked', 'totalOvertimeHoursWorked', 'standardRate', 'overtimeRate', 'standardWages', 'overtimeWages',
    'benefitRate1', 'benefitRate2', 'benefitRate3', 'hourlyPlusBenefit1', 'hourlyPlusBenefit2', 'hourlyPlusBenefit3',
    'davisBaconMin1', 'davisBaconMin2', 'davisBaconMin3', 'grossOthProj',
    'netWages', 'thisProjGross', 'allProjGross', 'exemptions'
  ];

  // Helper function to check if a field is numerical (including daily hours fields)
  const isNumericalField = (field) => {
    // Check if it's in the standard numerical fields list
    if (numericalFields.includes(field)) return true;

    // Check if it's a daily hours field (overtimeHours_1, standardHours_2, etc.)
    if (/^(overtimeHours|standardHours)_[1-7]$/.test(field)) return true;

    return false;
  };

  // Helper function to map API schema fields to frontend fields
  const mapApiToFrontendEmployee = (apiEmployee) => {
    const frontendEmployee = { ...apiEmployee };

    // Map API field names to frontend field names
    if (apiEmployee.totalStandardHoursWorked !== undefined) {
      frontendEmployee.totalStandardHoursWorked = apiEmployee.totalStandardHoursWorked;
    }
    if (apiEmployee.totalOvertimeHoursWorked !== undefined) {
      frontendEmployee.totalOvertimeHoursWorked = apiEmployee.totalOvertimeHoursWorked;
    }
    if (apiEmployee.thisProjGross !== undefined) {
      frontendEmployee.thisProjectGross = apiEmployee.thisProjGross;
    }
    if (apiEmployee.grossOthProj !== undefined) {
      frontendEmployee.grossOtherProjects = apiEmployee.grossOthProj;
    }
    if (apiEmployee.netWages !== undefined) {
      frontendEmployee.netPay = apiEmployee.netWages;
    }

    // Map deduction fields from nested structure if they exist
    if (apiEmployee.deductions) {
      frontendEmployee.ficaDeduction = apiEmployee.deductions.fica;
      frontendEmployee.withholding = apiEmployee.deductions.witholdingTax;
      frontendEmployee.otherDeduction1 = apiEmployee.deductions.other;
      frontendEmployee.otherDeduction2 = apiEmployee.deductions.healthInsurance;
      frontendEmployee.otherDeduction3 = apiEmployee.deductions.retirement_401k;
      // Map totalDeductions if available
      if (apiEmployee.deductions.totalDeductions !== undefined) {
        frontendEmployee.totalDeductions = apiEmployee.deductions.totalDeductions;
      }
    }

    return frontendEmployee;
  };

  // Helper function to map frontend fields back to API schema format
  const mapFrontendToApiEmployee = (frontendEmployee) => {
    const apiEmployee = { ...frontendEmployee };

    // Map frontend field names back to API field names
    if (frontendEmployee.totalStandardHoursWorked !== undefined) {
      apiEmployee.totalStandardHoursWorked = frontendEmployee.totalStandardHoursWorked;
    }
    if (frontendEmployee.totalOvertimeHoursWorked !== undefined) {
      apiEmployee.totalOvertimeHoursWorked = frontendEmployee.totalOvertimeHoursWorked;
    }
    if (frontendEmployee.thisProjectGross !== undefined) {
      apiEmployee.thisProjGross = frontendEmployee.thisProjectGross;
    }
    if (frontendEmployee.grossOtherProjects !== undefined) {
      apiEmployee.grossOthProj = frontendEmployee.grossOtherProjects;
    }
    if (frontendEmployee.netPay !== undefined) {
      apiEmployee.netWages = frontendEmployee.netPay;
    }

    // Map deduction fields to nested structure
    apiEmployee.deductions = {
      fica: frontendEmployee.ficaDeduction || 0,
      witholdingTax: frontendEmployee.withholding || 0,
      other: frontendEmployee.otherDeduction1 || 0,
      healthInsurance: frontendEmployee.otherDeduction2 || 0,
      retirement_401k: frontendEmployee.otherDeduction3 || 0,
      totalDeductions: frontendEmployee.totalDeductions || 0
    };

    return apiEmployee;
  };

  // Helper function to calculate payroll values for an employee
  const calculateEmployeePayroll = (employee) => {
    // Calculate total hours from daily entries if they exist
    let totalStandardHours = parseFloat(employee.totalStandardHoursWorked) || 0;
    let totalOvertimeHours = parseFloat(employee.totalOvertimeHoursWorked) || 0;

    // Sum up daily hours if they exist
    let dailyStandardTotal = 0;
    let dailyOvertimeTotal = 0;
    for (let day = 1; day <= 7; day++) {
      dailyStandardTotal += parseFloat(employee[`standardHours_${day}`]) || 0;
      dailyOvertimeTotal += parseFloat(employee[`overtimeHours_${day}`]) || 0;
    }

    // Use daily totals if they have values, otherwise use the total fields
    const standardHours = dailyStandardTotal > 0 ? dailyStandardTotal : totalStandardHours;
    const overtimeHours = dailyOvertimeTotal > 0 ? dailyOvertimeTotal : totalOvertimeHours;

    const standardRate = parseFloat(employee.standardRate) || 0;
    const overtimeRate = parseFloat(employee.overtimeRate) || 0;

    // Calculate wages based on hours and rates
    const standardWages = standardHours * standardRate;
    const overtimeWages = overtimeHours * overtimeRate;
    const hoursTimesRates = standardWages + overtimeWages; // All hours * respective rates

    // Get other values
    const grossOtherProjects = parseFloat(employee.grossOthProj) || 0;

    // Calculate total deductions from nested structure
    const ficaDeduction = parseFloat(employee.deductions?.fica) || 0;
    const withholding = parseFloat(employee.deductions?.witholdingTax) || 0;
    const otherDeduction = parseFloat(employee.deductions?.other) || 0;
    const healthInsurance = parseFloat(employee.deductions?.healthInsurance) || 0;
    const retirement401k = parseFloat(employee.deductions?.retirement_401k) || 0;
    const totalDeductions = ficaDeduction + withholding + otherDeduction + healthInsurance + retirement401k;

    // Calculate the three key values according to specifications:
    // 1. "This" value: All hours * respective rates (does not include grossOtherProjects)
    const thisProjectGross = hoursTimesRates;

    // 2. "All" value: All hours * respective rates + grossOtherProjects
    const allProjectsGross = hoursTimesRates + grossOtherProjects;

    // 3. Net Pay: All hours * respective rates + grossOtherProjects - deductions
    const netPay = allProjectsGross - totalDeductions;

    return {
      standardWages: standardWages.toFixed(2),
      overtimeWages: overtimeWages.toFixed(2),
      thisProjGross: thisProjectGross.toFixed(2),
      allProjGross: allProjectsGross.toFixed(2),
      totalDeductions: totalDeductions.toFixed(2),
      netWages: netPay.toFixed(2)
    };
  };

  // Helper function to apply calculated values to an employee
  const applyCalculatedValues = (employeeIndex) => {
    const employee = metadataFormData.employees[employeeIndex];
    if (!employee) return;

    const calculated = calculateEmployeePayroll(employee);

    // Calculate total hours from daily entries
    let dailyStandardTotal = 0;
    let dailyOvertimeTotal = 0;
    for (let day = 1; day <= 7; day++) {
      dailyStandardTotal += parseFloat(employee[`standardHours_${day}`]) || 0;
      dailyOvertimeTotal += parseFloat(employee[`overtimeHours_${day}`]) || 0;
    }

    const updatedEmployees = [...(metadataFormData.employees || [])];

    // Ensure deductions object exists
    if (!updatedEmployees[employeeIndex].deductions) {
      updatedEmployees[employeeIndex].deductions = {};
    }

    updatedEmployees[employeeIndex] = {
      ...updatedEmployees[employeeIndex],
      standardWages: calculated.standardWages,
      overtimeWages: calculated.overtimeWages,
      netWages: calculated.netWages,
      thisProjGross: calculated.thisProjGross,
      allProjGross: calculated.allProjGross,
      deductions: {
        ...updatedEmployees[employeeIndex].deductions,
        totalDeductions: calculated.totalDeductions
      },
      // Update total hours if daily hours exist
      ...(dailyStandardTotal > 0 && { totalStandardHoursWorked: dailyStandardTotal.toFixed(2) }),
      ...(dailyOvertimeTotal > 0 && { totalOvertimeHoursWorked: dailyOvertimeTotal.toFixed(2) })
    };

    handleMetadataChange('employees', updatedEmployees);
  };

  // Helper function to handle day worked changes
  const handleDayWorkedChange = (employeeIndex, dayIndex, field, value) => {
    const updatedEmployees = [...(metadataFormData.employees || [])];
    if (!updatedEmployees[employeeIndex]) {
      updatedEmployees[employeeIndex] = {};
    }
    if (!updatedEmployees[employeeIndex].daysWorked) {
      updatedEmployees[employeeIndex].daysWorked = [];
    }
    if (!updatedEmployees[employeeIndex].daysWorked[dayIndex]) {
      updatedEmployees[employeeIndex].daysWorked[dayIndex] = {};
    }
    updatedEmployees[employeeIndex].daysWorked[dayIndex][field] = value;
    handleMetadataChange('employees', updatedEmployees);
  };

  // Helper function to add a new employee
  const addEmployee = () => {
    const newEmployee = {
      firstName: '',
      middleName: '',
      lastName: '',
      last4ssn: '',
      exemptions: 0,
      jobTitle: '',
      daysWorked: [{
        dayOfWeek: '',
        date: '',
        standardHours: 0,
        overtimeHours: 0
      }],
      totalHoursWorked: 0,
      standardRate: 0,
      overtimeRate: 0,
      wages: 0,
      allProjGross: 0,
      deductions: {
        fica: 0,
        witholdingTax: 0,
        healthInsurance: 0,
        retirement_401k: 0,
        other: 0,
        totalDeductions: 0
      },
      netWages: 0
    };
    const updatedEmployees = [...(metadataFormData.employees || []), newEmployee];
    handleMetadataChange('employees', updatedEmployees);
  };

  // Helper function to remove an employee
  const removeEmployee = (employeeIndex) => {
    const updatedEmployees = (metadataFormData.employees || []).filter((_, index) => index !== employeeIndex);
    handleMetadataChange('employees', updatedEmployees);
  };

  // Helper function to add a day worked to an employee
  const addDayWorked = (employeeIndex) => {
    const updatedEmployees = [...(metadataFormData.employees || [])];
    if (!updatedEmployees[employeeIndex].daysWorked) {
      updatedEmployees[employeeIndex].daysWorked = [];
    }
    updatedEmployees[employeeIndex].daysWorked.push({
      dayOfWeek: '',
      date: '',
      standardHours: 0,
      overtimeHours: 0
    });
    handleMetadataChange('employees', updatedEmployees);
  };

  // Render WH-347 form structure
  const renderWH347Form = () => {
    if (formData.form_type !== 'WH-347_Rev_December_2008') {
      return renderGenericMetadataForm();
    }

    return (
      <div className="wh347-legacy-form">
        {/* Form Header */}
        <div className="legacy-header">
          <h3>U.S. Department of Labor - WH-347 Payroll Form (Legacy Layout)</h3>
        </div>

        {/* Validation Error Summary */}
        {(() => {
          const historicalErrors = extractValidationErrors(documentReviews);
          const currentErrors = new Set();

          // Check for real-time validation errors
          const fieldsToCheck = ['contractorName', 'subcontractorName', 'projectName', 'forWeekEnding', 'payrollStartDate', 'payrollEndDate'];
          fieldsToCheck.forEach(field => {
            // Don't show real-time validation for fields that have been successfully validated
            // Check if this field had an error in historical validation
            const hadHistoricalError = Array.from(historicalErrors).some(error =>
              error.includes(field) || error.toLowerCase().includes(field.toLowerCase())
            );

            // Only show real-time validation errors if:
            // 1. The field didn't have a historical error (so it's a new issue), OR
            // 2. The field had a historical error AND the current value fails validation
            if (!hadHistoricalError || validateFieldSync(field, metadataFormData[field])) {
              // Only add to current errors if validation actually fails
              if (validateFieldSync(field, metadataFormData[field])) {
                currentErrors.add(`${field} (current input)`);
              }
            }
          });

          // Check employee fields
          (metadataFormData.employees || []).forEach((employee, index) => {
            const employeeFields = ['firstName', 'lastName', 'jobTitle'];
            employeeFields.forEach(field => {
              if (hasValidationError(field, index)) {
                currentErrors.add(`Employee ${index + 1} ${field}`);
              }
            });
          });

          const allErrors = new Set([...historicalErrors, ...currentErrors]);

          if (allErrors.size > 0) {
            return (
              <div className="validation-error-summary">
                <h5>⚠ Validation Issues Found</h5>
                <p>The following fields have validation errors and need to be corrected:</p>
                <ul className="validation-error-list">
                  {Array.from(allErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        })()}

        {/* Project Information Section */}
        <div className="project-info-legacy">
          <h4>Project Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Company Type:</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="companyType"
                    value="contractor"
                    checked={metadataFormData.companyType === 'contractor' || (!metadataFormData.companyType && metadataFormData.contractorName && !metadataFormData.subcontractorName)}
                    onChange={() => {
                      handleMetadataChange('companyType', 'contractor');
                      // Inherit name from subcontractor if switching from subcontractor to contractor
                      if (metadataFormData.subcontractorName && !metadataFormData.contractorName) {
                        handleMetadataChange('contractorName', metadataFormData.subcontractorName);
                      }
                    }}
                  />
                  Contractor
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="companyType"
                    value="subcontractor"
                    checked={metadataFormData.companyType === 'subcontractor' || (!metadataFormData.companyType && metadataFormData.subcontractorName && !metadataFormData.contractorName)}
                    onChange={() => {
                      handleMetadataChange('companyType', 'subcontractor');
                      // Inherit name from contractor if switching from contractor to subcontractor
                      if (metadataFormData.contractorName && !metadataFormData.subcontractorName) {
                        handleMetadataChange('subcontractorName', metadataFormData.contractorName);
                      }
                    }}
                  />
                  Subcontractor
                </label>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              {(metadataFormData.companyType === 'contractor' || (!metadataFormData.companyType && metadataFormData.contractorName && !metadataFormData.subcontractorName)) && (
                <>
                  <label className={hasValidationError('contractorName') ? 'field-error-label' : ''}>
                    Contractor Name:
                    {hasValidationError('contractorName') && <span className="field-error-icon">⚠</span>}
                  </label>
                  <input
                    type="text"
                    className={hasValidationError('contractorName') ? 'field-error' : ''}
                    value={metadataFormData.contractorName || ''}
                    onChange={(e) => handleMetadataChange('contractorName', e.target.value)}
                  />
                  <SuggestionsDropdown
                    suggestions={suggestions.contractorName.items}
                    fieldName="contractorName"
                    originalValue={suggestions.contractorName.originalValue}
                    onSelect={(value) => handleSuggestionSelect('contractorName', value)}
                    onCreateRule={handleCreateMatchingRule}
                    isVisible={suggestions.contractorName.visible}
                  />
                  <RuleNotification 
                    fieldName="contractorName" 
                    ruleInfo={ruleReplacements.contractorName} 
                  />
                </>
              )}
              {(metadataFormData.companyType === 'subcontractor' || (!metadataFormData.companyType && metadataFormData.subcontractorName && !metadataFormData.contractorName)) && (
                <>
                  <label className={hasValidationError('subcontractorName') ? 'field-error-label' : ''}>
                    Subcontractor Name:
                    {hasValidationError('subcontractorName') && <span className="field-error-icon">⚠</span>}
                  </label>
                  <input
                    type="text"
                    className={hasValidationError('subcontractorName') ? 'field-error' : ''}
                    value={metadataFormData.subcontractorName || ''}
                    onChange={(e) => handleMetadataChange('subcontractorName', e.target.value)}
                  />
                  <SuggestionsDropdown
                    suggestions={suggestions.subcontractorName.items}
                    fieldName="subcontractorName"
                    originalValue={suggestions.subcontractorName.originalValue}
                    onSelect={(value) => handleSuggestionSelect('subcontractorName', value)}
                    onCreateRule={handleCreateMatchingRule}
                    isVisible={suggestions.subcontractorName.visible}
                  />
                  <RuleNotification 
                    fieldName="subcontractorName" 
                    ruleInfo={ruleReplacements.subcontractorName} 
                  />
                </>
              )}
            </div>
            <div className="form-group">
              <label className={hasValidationError('projectName') ? 'field-error-label' : ''}>
                Project Name:
                {hasValidationError('projectName') && <span className="field-error-icon">⚠</span>}
              </label>
              <input
                type="text"
                className={hasValidationError('projectName') ? 'field-error' : ''}
                value={metadataFormData.projectName || ''}
                onChange={(e) => handleMetadataChange('projectName', e.target.value)}
              />
              <SuggestionsDropdown
                suggestions={suggestions.projectName.items}
                fieldName="projectName"
                originalValue={suggestions.projectName.originalValue}
                onSelect={(value) => handleSuggestionSelect('projectName', value)}
                onCreateRule={handleCreateMatchingRule}
                isVisible={suggestions.projectName.visible}
              />
              <RuleNotification 
                fieldName="projectName" 
                ruleInfo={ruleReplacements.projectName} 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contract No:</label>
              <input
                type="text"
                value={metadataFormData.contractNo || ''}
                onChange={(e) => handleMetadataChange('contractNo', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Payroll No:</label>
              <input
                type="text"
                value={metadataFormData.payrollNo || ''}
                onChange={(e) => handleMetadataChange('payrollNo', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className={hasValidationError('forWeekEnding') ? 'field-error-label' : ''}>
                For Week Ending:
                {hasValidationError('forWeekEnding') && <span className="field-error-icon">⚠</span>}
              </label>
              <input
                type="text"
                className={hasValidationError('forWeekEnding') ? 'field-error' : ''}
                value={metadataFormData.forWeekEnding || ''}
                onChange={(e) => handleMetadataChange('forWeekEnding', e.target.value)}
                placeholder="MM/DD/YYYY"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className={hasValidationError('payrollStartDate') ? 'field-error-label' : ''}>
                Payroll Start Date:
                {hasValidationError('payrollStartDate') && <span className="field-error-icon">⚠</span>}
              </label>
              <input
                type="text"
                className={hasValidationError('payrollStartDate') ? 'field-error' : ''}
                value={metadataFormData.payrollStartDate || ''}
                onChange={(e) => handleMetadataChange('payrollStartDate', e.target.value)}
                placeholder="MM/DD/YYYY"
              />
            </div>
            <div className="form-group">
              <label className={hasValidationError('payrollEndDate') ? 'field-error-label' : ''}>
                Payroll End Date:
                {hasValidationError('payrollEndDate') && <span className="field-error-icon">⚠</span>}
              </label>
              <input
                type="text"
                className={hasValidationError('payrollEndDate') ? 'field-error' : ''}
                value={metadataFormData.payrollEndDate || ''}
                onChange={(e) => handleMetadataChange('payrollEndDate', e.target.value)}
                placeholder="MM/DD/YYYY"
              />
            </div>
          </div>
        </div>

        {/* Legacy Table Layout */}
        <table className="wh347-legacy-table">
          <thead>
            <tr>
              <th className="name-title-col">
                (1)(2)<br/>NAME - TITLE
              </th>
              <th className="day-date-col">
                (4) DAY AND DATE<br/>
                <table className="day-header-table">
                  <tbody>
                    <tr>
                      <td className="day-spacer"></td>
                      {generateDateHeadersFromDaysWorked(metadataFormData.employees, metadataFormData.payrollStartDate).map((dayInfo, index) => (
                        <td key={index} className="day-header">{dayInfo.date}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="day-spacer"></td>
                      {generateDateHeadersFromDaysWorked(metadataFormData.employees, metadataFormData.payrollStartDate).map((dayInfo, index) => (
                        <td key={index} className="day-header">{dayInfo.day}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </th>
              <th className="hours-rate-col">
                (5) TOTAL HOURS<br/>HOURLY RATE<br/>EARNED WAGES<br/>
                <small>Hours | Rate | Earned</small>
              </th>
              <th className="benefit-rate-col">
                (6)(7) BENEFIT RATE<br/>HOURLY + BENEFIT RATE<br/>DAVIS-BACON MIN RATE<br/>
                <small>Benefit|Rate+Ben|DB Min</small>
              </th>
              <th className="deductions-col">
                (8) DEDUCTIONS
                <table className="deduction-header-table">
                  <tbody>
                    <tr>
                      <td className="ded-header">FICA</td>
                      <td className="ded-header">W/H</td>
                      <td className="ded-header">OTH1</td>
                      <td className="ded-header">OTH2</td>
                      <td className="ded-header">OTH3</td>
                      <td className="ded-header">TOTAL</td>
                    </tr>
                  </tbody>
                </table>
              </th>
              <th className="net-wages-col">
                (9) NET<br/>WAGES<br/>ALL PRJ
              </th>
            </tr>
          </thead>
          <tbody>
            {(metadataFormData.employees || []).map((employee, index) => (
              <tr key={index} className="legacy-employee-row">
                {/* Name/Title Column */}
                <td className="name-title-cell">
                  <div className="employee-name-section">
                    <input
                      type="text"
                      className={hasValidationError('firstName', index) || hasValidationError('lastName', index) ? 'field-error' : ''}
                      value={employee._fullNameInput !== undefined ? employee._fullNameInput : `${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                      onChange={(e) => {
                        // Store the raw input value temporarily without parsing
                        handleEmployeeChange(index, '_fullNameInput', e.target.value);
                      }}
                      onBlur={(e) => {
                        // Only parse the name when user finishes typing (loses focus)
                        const names = e.target.value.trim().split(/\s+/);
                        const firstName = names[0] || '';
                        const lastName = names.slice(1).join(' ') || '';
                        handleEmployeeChange(index, 'firstName', firstName);
                        handleEmployeeChange(index, 'lastName', lastName);
                        // Clear the temporary field
                        handleEmployeeChange(index, '_fullNameInput', undefined);
                      }}
                      placeholder="Employee Name"
                      className="legacy-name-input"
                    />
                    <input
                      type="text"
                      value={employee.jobTitle || ''}
                      onChange={(e) => handleEmployeeChange(index, 'jobTitle', e.target.value)}
                      onBlur={(e) => {
                        // Job title validation will be handled by the real-time correction system
                      }}
                      placeholder="Job Title"
                      className={`legacy-title-input ${hasValidationError('jobTitle', index) ? 'field-error' : ''}`}
                    />
                    <SuggestionsDropdown
                      suggestions={suggestions.jobTitle[index]?.items || []}
                      fieldName="jobTitle"
                      originalValue={suggestions.jobTitle[index]?.originalValue || ''}
                      onSelect={(value) => handleSuggestionSelect('jobTitle', value, index)}
                      onCreateRule={(ruleData) => handleCreateMatchingRule({...ruleData, employeeIndex: index})}
                      isVisible={suggestions.jobTitle[index]?.visible || false}
                    />
                    <RuleNotification 
                      fieldName="jobTitle" 
                      ruleInfo={ruleReplacements.jobTitle[index]} 
                    />
                  </div>
                  <div className="ssn-exemptions">
                    <label className="legacy-label">Last 4 SSN:</label>
                    <input
                      type="text"
                      value={formatLast4SSN(employee.last4ssn)}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        // Store the raw numeric value, but display will be formatted
                        const numericValue = rawValue.replace(/\D/g, '');
                        if (numericValue === '') {
                          handleEmployeeChange(index, 'last4ssn', '');
                        } else {
                          const num = parseInt(numericValue, 10);
                          if (!isNaN(num) && num >= 0 && num <= 9999) {
                            handleEmployeeChange(index, 'last4ssn', num);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure proper formatting on blur
                        const currentValue = employee.last4ssn;
                        if (currentValue || currentValue === 0) {
                          handleEmployeeChange(index, 'last4ssn', currentValue);
                        }
                      }}
                      placeholder="0000"
                      maxLength="4"
                      className="legacy-ssn-input"
                    />
                    <br/>
                    <label className="legacy-label">(3) Exemptions:</label>
                    <input
                      type="text"
                      value={employee.exemptions || ''}
                      onChange={(e) => handleEmployeeChange(index, 'exemptions', e.target.value)}
                      className="legacy-exemptions-input"
                      placeholder="0"
                    />
                  </div>
                </td>

                {/* Hours Per Day Column */}
                <td className="day-hours-cell">
                  <table className="legacy-hours-table">
                    <tbody>
                      <tr>
                        <td className="hour-type-label">O</td>
                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                          <td key={day}>
                            <input
                              {...getNumericInputProps(
                                employee[`overtimeHours_${day}`],
                                (e) => handleEmployeeChange(index, `overtimeHours_${day}`, e.target.value),
                                index,
                                `overtimeHours_${day}`
                              )}
                              className="legacy-daily-hours"
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="hour-type-label">S</td>
                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                          <td key={day}>
                            <input
                              {...getNumericInputProps(
                                employee[`standardHours_${day}`],
                                (e) => handleEmployeeChange(index, `standardHours_${day}`, e.target.value),
                                index,
                                `standardHours_${day}`
                              )}
                              className="legacy-daily-hours"
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  <div className="hours-worked-label">HOURS WORKED EACH DAY</div>
                </td>

                {/* Total Hours/Rate/Wages Column */}
                <td className="hours-rate-cell">
                  <table className="legacy-rate-table">
                    <tbody>
                      <tr>
                        <td className="rate-type-label">OT:</td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.totalOvertimeHoursWorked,
                              (e) => handleEmployeeChange(index, 'totalOvertimeHoursWorked', e.target.value),
                              index,
                              'totalOvertimeHoursWorked'
                            )}
                            className="legacy-hours-input"
                          />
                        </td>
                        <td>$</td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.overtimeRate,
                              (e) => handleEmployeeChange(index, 'overtimeRate', e.target.value),
                              index,
                              'overtimeRate'
                            )}
                            className="legacy-rate-input"
                          />
                        </td>
                        <td>$</td>
                        <td>
                          <input
                            type="text"
                            value={employee.overtimeWages || ''}
                            onChange={(e) => handleEmployeeChange(index, 'overtimeWages', e.target.value)}
                            className="legacy-ot-wages-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="rate-type-label">ST:</td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.totalStandardHoursWorked,
                              (e) => handleEmployeeChange(index, 'totalStandardHoursWorked', e.target.value),
                              index,
                              'totalStandardHoursWorked'
                            )}
                            className="legacy-hours-input"
                          />
                        </td>
                        <td>$</td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.standardRate,
                              (e) => handleEmployeeChange(index, 'standardRate', e.target.value),
                              index,
                              'standardRate'
                            )}
                            className="legacy-rate-input"
                          />
                        </td>
                        <td>$</td>
                        <td>
                          <input
                            type="text"
                            value={employee.standardWages || ''}
                            onChange={(e) => handleEmployeeChange(index, 'standardWages', e.target.value)}
                            className="legacy-std-wages-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td></td>
                        <td className="total-hours">
                          {((parseFloat(employee.totalStandardHoursWorked) || 0) + (parseFloat(employee.totalOvertimeHoursWorked) || 0)).toFixed(2)}
                        </td>
                        <td colSpan="2" className="project-label">This Prj:</td>
                        <td>$</td>
                        <td className="gross-pay">
                          {(parseFloat(employee.thisProjGross) || ((parseFloat(employee.standardWages) || parseFloat(employee.wages) || 0) + (parseFloat(employee.overtimeWages) || 0))).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>

                {/* Benefit Rates Column */}
                <td className="benefit-cell">
                  <table className="legacy-benefit-table">
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="text"
                            value={employee.benefitRate1 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'benefitRate1', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={employee.hourlyPlusBenefit1 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'hourlyPlusBenefit1', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={employee.davisBaconMin1 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'davisBaconMin1', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            value={employee.benefitRate2 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'benefitRate2', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={employee.hourlyPlusBenefit2 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'hourlyPlusBenefit2', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={employee.davisBaconMin2 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'davisBaconMin2', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            value={employee.benefitRate3 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'benefitRate3', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={employee.hourlyPlusBenefit3 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'hourlyPlusBenefit3', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={employee.davisBaconMin3 || ''}
                            onChange={(e) => handleEmployeeChange(index, 'davisBaconMin3', e.target.value)}
                            className="legacy-benefit-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="gross-other">
                          Gross Oth Prj: $
                          <input
                            type="text"
                            value={employee.grossOthProj || ''}
                            onChange={(e) => handleEmployeeChange(index, 'grossOthProj', e.target.value)}
                            className="legacy-gross-other"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>

                {/* Deductions Column */}
                <td className="deductions-cell">
                  <table className="legacy-deductions-table">
                    <tbody>
                      <tr>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.deductions?.fica,
                              (e) => handleDeductionChange(index, 'fica', e.target.value),
                              index,
                              'deductions.fica'
                            )}
                            className="legacy-deduction-fica"
                          />
                        </td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.deductions?.witholdingTax,
                              (e) => handleDeductionChange(index, 'witholdingTax', e.target.value),
                              index,
                              'deductions.witholdingTax'
                            )}
                            className="legacy-deduction-witholdingTax"
                          />
                        </td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.deductions?.other,
                              (e) => handleDeductionChange(index, 'other', e.target.value),
                              index,
                              'deductions.other'
                            )}
                            className="legacy-deduction-other"
                          />
                        </td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.deductions?.healthInsurance,
                              (e) => handleDeductionChange(index, 'healthInsurance', e.target.value),
                              index,
                              'deductions.healthInsurance'
                            )}
                            className="legacy-deduction-healthInsurance"
                          />
                        </td>
                        <td>
                          <input
                            {...getNumericInputProps(
                              employee.deductions?.retirement_401k,
                              (e) => handleDeductionChange(index, 'retirement_401k', e.target.value),
                              index,
                              'deductions.retirement_401k'
                            )}
                            className="legacy-deduction-retirement_401k"
                          />
                        </td>
                        <td className="total-deductions">
                          {(parseFloat(employee.deductions?.totalDeductions) || ((parseFloat(employee.deductions?.fica) || 0) + (parseFloat(employee.deductions?.witholdingTax) || 0) + (parseFloat(employee.deductions?.other) || 0) + (parseFloat(employee.deductions?.healthInsurance) || 0) + (parseFloat(employee.deductions?.retirement_401k) || 0))).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="note-section">
                    <label>Note:</label>
                    <input
                      type="text"
                      value={employee.note || ''}
                      onChange={(e) => handleEmployeeChange(index, 'note', e.target.value)}
                      className="legacy-note-input"
                      placeholder="Enter note"
                    />
                  </div>
                </td>

                {/* Net Wages Column */}
                <td className="net-wages-cell">
                  <div className="net-pay">
                    $
                    <input
                      type="text"
                      value={employee.netWages || ''}
                      onChange={(e) => handleEmployeeChange(index, 'netWages', e.target.value)}
                      className="legacy-net-pay"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="gross-pay-summary">
                    <table className="legacy-gross-summary">
                      <tbody>
                        <tr><td colSpan="2" className="summary-header">Prj Gross Pay</td></tr>
                        <tr>
                          <td>All:</td>
                          <td>
                            $<input
                              {...getNumericInputProps(
                                employee.allProjGross,
                                (e) => handleEmployeeChange(index, 'allProjGross', e.target.value),
                                index,
                                'allProjGross'
                              )}
                              className="legacy-gross-input"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>This:</td>
                          <td>
                            $<input
                              {...getNumericInputProps(
                                employee.thisProjGross,
                                (e) => handleEmployeeChange(index, 'thisProjGross', e.target.value),
                                index,
                                'thisProjGross'
                              )}
                              className="legacy-gross-input"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEmployee(index)}
                    className="legacy-remove-btn"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Employee Management */}
        <div className="legacy-employee-management">
          <button type="button" onClick={addEmployee} className="legacy-add-btn">
            Add Employee
          </button>
          <button type="button" className="legacy-clear-btn">
            Clear Form
          </button>
        </div>

        {/* Additional Options */}
        <div className="legacy-additional-options">
          <h4>Additional Options</h4>
          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={metadataFormData.fringeCheckbox_a === 'checked'}
                  onChange={(e) => handleMetadataChange('fringeCheckbox_a', e.target.checked ? 'checked' : 'unchecked')}
                />
                Fringe Benefits (Option A)
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={metadataFormData.friendCheckbox_b === 'checked'}
                  onChange={(e) => handleMetadataChange('friendCheckbox_b', e.target.checked ? 'checked' : 'unchecked')}
                />
                Alternative Benefits (Option B)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="exceptions">Exceptions (if any):</label>
            <textarea
              id="exceptions"
              value={Array.isArray(metadataFormData.exceptions) ? metadataFormData.exceptions.join(', ') : metadataFormData.exceptions || ''}
              onChange={(e) => handleMetadataChange('exceptions', e.target.value.split(', ').filter(ex => ex.trim()))}
              placeholder="Enter any exceptions, separated by commas"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="remarks">Remarks:</label>
            <textarea
              id="remarks"
              value={metadataFormData.remarks || ''}
              onChange={(e) => handleMetadataChange('remarks', e.target.value)}
              placeholder="Additional remarks or comments"
              rows={3}
            />
          </div>
        </div>
      </div>
    );
  };

  // Fallback for non-WH-347 forms
  const renderGenericMetadataForm = () => {
    return (
      <div className="generic-metadata-form">
        {Object.entries(metadataFormData).map(([key, value]) => {
          if (Array.isArray(value)) {
            return (
              <div key={key} className="form-group">
                <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</label>
                <textarea
                  value={JSON.stringify(value, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleMetadataChange(key, parsed);
                    } catch {
                      handleMetadataChange(key, e.target.value);
                    }
                  }}
                  rows={4}
                  placeholder="JSON array data"
                />
              </div>
            );
          } else if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="form-group">
                <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</label>
                <textarea
                  value={JSON.stringify(value, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleMetadataChange(key, parsed);
                    } catch {
                      handleMetadataChange(key, e.target.value);
                    }
                  }}
                  rows={3}
                  placeholder="JSON object data"
                />
              </div>
            );
          } else {
            return (
              <div key={key} className="form-group">
                <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</label>
                <input
                  type={typeof value === 'number' ? 'number' : 'text'}
                  value={value || ''}
                  onChange={(e) => handleMetadataChange(key, e.target.value)}
                />
              </div>
            );
          }
        })}
      </div>
    );
  };

  // Validation function to check critical fields before submission
  const validateCriticalFields = () => {
    const errors = [];

    // Validate contractor/subcontractor name based on company type
    if (metadataFormData.companyType === 'contractor') {
      if (!metadataFormData.contractorName?.trim()) {
        errors.push('Contractor Name is required');
      }
    } else if (metadataFormData.companyType === 'subcontractor') {
      if (!metadataFormData.subcontractorName?.trim()) {
        errors.push('Subcontractor Name is required');
      }
    } else {
      // Legacy fallback validation - require at least one contractor field
      if (!metadataFormData.contractorName?.trim() && !metadataFormData.subcontractorName?.trim()) {
        errors.push('Either Contractor Name or Subcontractor Name is required');
      }
    }

    // Validate other critical fields
    if (!metadataFormData.projectName?.trim()) {
      errors.push('Project Name is required');
    }

    if (!metadataFormData.forWeekEnding?.trim()) {
      errors.push('Week Ending date is required');
    }

    // Validate employees array is not empty and has valid data
    if (!metadataFormData.employees || metadataFormData.employees.length === 0) {
      errors.push('At least one employee is required');
    } else {
      // Check each employee for required fields
      metadataFormData.employees.forEach((employee, index) => {
        if (!employee.firstName?.trim()) {
          errors.push(`Employee ${index + 1}: First Name is required`);
        }
        if (!employee.lastName?.trim()) {
          errors.push(`Employee ${index + 1}: Last Name is required`);
        }
        if (!employee.jobTitle?.trim()) {
          errors.push(`Employee ${index + 1}: Job Title is required`);
        }
      });
    }

    return errors.length > 0 ? errors.join('; ') : null;
  };

  const handleSaveCorrectionComments = async (e) => {
    e.preventDefault();
    
    if (!correctionComments?.trim()) {
      setError('Please enter some correction comments to save.');
      return;
    }

    setSavingComments(true);
    setError(null);

    try {
      // Find the most recent document review to update
      if (!documentReviews || documentReviews.length === 0) {
        setError('No document review found to update.');
        setSavingComments(false);
        return;
      }

      const mostRecentReview = documentReviews[0];
      
      // Parse existing correction comments as array, or create new array
      let existingComments = [];
      if (mostRecentReview.correction_comments) {
        try {
          // Try to parse as JSON array
          existingComments = JSON.parse(mostRecentReview.correction_comments);
          if (!Array.isArray(existingComments)) {
            // If it's not an array, treat the existing text as a single comment
            existingComments = [mostRecentReview.correction_comments];
          }
        } catch {
          // If JSON parsing fails, treat as single string comment
          existingComments = [mostRecentReview.correction_comments];
        }
      }
      
      // Create new comment entry with timestamp and actual user name
      const userName = user ? 
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'User' 
        : 'User';
      
      const newComment = {
        comment: correctionComments.trim(),
        timestamp: new Date().toISOString(),
        user: userName
      };
      
      // Append to existing comments
      const updatedComments = [...existingComments, newComment];
      
      // Use the document PUT endpoint to update correction comments
      // Convert legacy daily hours format back to daysWorked for API
      const employeesForApi = convertLegacyFormatToDaysWorked(metadataFormData.employees || []);
      
      const requestData = {
        title: document.title,
        uploader_id: document.uploader_id,
        form_type: document.form_type,
        status: document.status, // Keep current status
        correction_comments: JSON.stringify(updatedComments),
        // Include all required metadata fields
        contractorName: metadataFormData.contractorName || '',
        subcontractorName: metadataFormData.subcontractorName || '',
        projectName: metadataFormData.projectName || '',
        contractNo: metadataFormData.contractNo || '',
        payrollNo: parseInt(metadataFormData.payrollNo) || 0,
        forWeekEnding: metadataFormData.forWeekEnding || '',
        payrollStartDate: metadataFormData.payrollStartDate || '',
        payrollEndDate: metadataFormData.payrollEndDate || '',
        dateSigned: metadataFormData.dateSigned || '',
        signatoryName: metadataFormData.signatoryName || '',
        signatoryTitle: metadataFormData.signatoryTitle || '',
        fringeCheckbox_a: metadataFormData.fringeCheckbox_a || 'unchecked',
        friendCheckbox_b: metadataFormData.friendCheckbox_b || 'unchecked',
        employees: employeesForApi,
        exceptions: metadataFormData.exceptions || [],
        remarks: metadataFormData.remarks || '',
        bTestData: false
      };

      const response = await axios.put('/documents', requestData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        // Show success message
        setError({
          type: 'success',
          message: 'Correction comments saved successfully!'
        });

        // Refresh the document reviews to show the updated comment
        const reviewsResponse = await axios.get(`/documents/${documentId}/reviews`, {
          headers: getAuthHeaders()
        });
        
        if (reviewsResponse.status === 200) {
          setDocumentReviews(reviewsResponse.data);
        }

        // Clear the input field after successful save
        setCorrectionComments('');

        // Auto-clear success message after 3 seconds
        setTimeout(() => {
          setError(null);
        }, 3000);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'Error saving correction comments'));
    } finally {
      setSavingComments(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!correctionComments?.trim()) {
      setError('Correction comments are required for rejecting a document.');
      return;
    }

    setButtonStates(prev => ({ ...prev, reject: true }));
    setError(null);

    try {

      // Use a direct status update for rejection - no employee processing
      const requestData = {
        title: formData.title,
        uploader_id: formData.uploader_id,
        form_type: formData.form_type,
        status: 'rejected',
        correction_comments: correctionComments?.trim()
      };


      // Submit the document with rejected status using the PUT /documents endpoint
      const response = await axios.put('/documents', requestData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        const result = response.data;
        if (!handleProcessingResult(result, 'rejected', 'rejection')) {
          return; // Don't close modal on error - error is displayed inline
        }
      }

      // Only close modal if no errors occurred
      if (onDocumentUpdated) {
        onDocumentUpdated();
      }

      onClose();
    } catch (err) {
      setError(formatErrorMessage(err, 'Error rejecting document'));
    } finally {
      setButtonStates(prev => ({ ...prev, reject: false }));
    }
  };

  const handleApprove = async (e) => {
    e.preventDefault();

    // Validate critical fields before approval
    const validationError = validateCriticalFields();
    if (validationError) {
      setError(`Please fix the following issues before approving: ${validationError}`);
      return;
    }

    setButtonStates(prev => ({ ...prev, approve: true }));
    setError(null);

    try {
      // Step 0: Immediately transition document to "processing" status
      console.log('Setting document status to processing...');
      
      // Create a new processing review entry and add it to the local state
      const processingReview = {
        review_id: `temp-processing-${Date.now()}`, // Temporary ID
        document_id: parseInt(documentId),
        reviewer_id: "admin", // Could get from user context
        previous_status: documentReviews.length > 0 ? (documentReviews[0].new_status || documentReviews[0].status) : 'review_required',
        new_status: 'processing',
        status: 'processing', // For compatibility
        current_status: 'processing', // For compatibility
        comments: 'Document approved and being processed...',
        review_date: new Date().toISOString(),
        correction_comments: correctionComments?.trim() || null
      };

      // Update local state immediately to show processing status
      setDocumentReviews(prev => [processingReview, ...prev]);

      // Step 1: Always save the current metadata state before approval
      // This ensures that any rule applications, corrections, or pre-populated data is preserved
      console.log('Saving current metadata state before approval...');
      
      // Prepare metadata update request with current form data
      const employeesForAPI = metadataFormData.employees
        ? convertLegacyFormatToDaysWorked(metadataFormData.employees)
        : [];

      const metadataUpdateData = {
        title: formData.title,
        uploader_id: formData.uploader_id,
        form_type: formData.form_type,
        status: 'review_required', // Keep status as review_required during metadata save
        payrollNo: metadataFormData.payrollNo || 0,
        forWeekEnding: metadataFormData.forWeekEnding || '',
        projectName: metadataFormData.projectName || '',
        contractNo: metadataFormData.contractNo || '',
        employees: employeesForAPI,
        dateSigned: metadataFormData.dateSigned || '',
        signatoryName: metadataFormData.signatoryName || '',
        signatoryTitle: metadataFormData.signatoryTitle || '',
        payrollStartDate: metadataFormData.payrollStartDate || '',
        payrollEndDate: metadataFormData.payrollEndDate || '',
        fringeCheckbox_a: metadataFormData.fringeCheckbox_a || 'unchecked',
        friendCheckbox_b: metadataFormData.friendCheckbox_b || 'unchecked',
        exceptions: metadataFormData.exceptions || [],
        remarks: metadataFormData.remarks || '',
        correction_comments: correctionComments?.trim() || null
      };

      // Include contractor or subcontractor based on company type
      if (metadataFormData.companyType === 'contractor') {
        metadataUpdateData.contractorName = metadataFormData.contractorName || '';
      } else if (metadataFormData.companyType === 'subcontractor') {
        metadataUpdateData.subcontractorName = metadataFormData.subcontractorName || '';
      } else {
        // Legacy fallback
        if (metadataFormData.contractorName) {
          metadataUpdateData.contractorName = metadataFormData.contractorName;
        } else if (metadataFormData.subcontractorName) {
          metadataUpdateData.subcontractorName = metadataFormData.subcontractorName;
        }
      }

      // Save metadata changes using PUT /documents endpoint
      const metadataResponse = await axios.put('/documents', metadataUpdateData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (metadataResponse.status !== 200) {
        setError('Failed to save metadata before approval');
        return;
      }

      console.log('Metadata saved successfully');
      
      // Wait a moment for the server to process the metadata update
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Now proceed with approval and employee processing
      console.log('Proceeding with document approval and employee processing...');
      
      const response = await axios.post(`/documents/${documentId}/approve`,
        { approver_id: "admin" }, // You could get this from user context
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Step 3: Update document review status based on API response
        console.log('Updating document status based on API response...', result);
        
        // Determine final status from API response
        let finalStatus = 'processed'; // Default
        let finalComments = 'Document approved and employees processed successfully';
        
        if (result.status) {
          finalStatus = result.status;
        }
        
        if (result.message) {
          finalComments = result.message;
        }
        
        // Create final review entry
        const finalReview = {
          review_id: `temp-final-${Date.now()}`,
          document_id: parseInt(documentId),
          reviewer_id: "admin",
          previous_status: 'processing',
          new_status: finalStatus,
          status: finalStatus,
          current_status: finalStatus,
          comments: finalComments,
          review_date: new Date().toISOString(),
          correction_comments: correctionComments?.trim() || null
        };
        
        // Update local state with final status (replace the processing entry)
        setDocumentReviews(prev => [finalReview, ...prev.slice(1)]);
        
        if (!handleProcessingResult(result, 'metadata saved, approved and employees processed', 'approval')) {
          return; // Don't close modal on error - error is displayed inline
        }
      }

      // Only close modal if no errors occurred
      if (onDocumentUpdated) {
        onDocumentUpdated();
      }

      onClose();
    } catch (err) {
      console.error('Error during approval process:', err);
      
      // Step 3b: Update status to error if the API call failed
      const errorReview = {
        review_id: `temp-error-${Date.now()}`,
        document_id: parseInt(documentId),
        reviewer_id: "admin",
        previous_status: 'processing',
        new_status: 'error',
        status: 'error',
        current_status: 'error',
        comments: `Processing failed: ${err.response?.data?.detail || err.message || 'Unknown error'}`,
        review_date: new Date().toISOString(),
        correction_comments: correctionComments?.trim() || null
      };
      
      // Update local state with error status (replace the processing entry)
      setDocumentReviews(prev => [errorReview, ...prev.slice(1)]);
      
      setError(formatErrorMessage(err, 'Error approving document'));
    } finally {
      setButtonStates(prev => ({ ...prev, approve: false }));
    }
  };

  const handleStatusAction = async (status, shouldReprocess, shouldCloseModal = true) => {
    setSaving(true);
    setError(null);

    // Set status based on action
    setFormData(prev => ({
      ...prev,
      status: status
    }));

    try {
      let requestData;

      if (shouldReprocess) {
        // For reprocessing (Correct & Resubmit): include all metadata and form data
        const employeesForAPI = metadataFormData.employees
          ? convertLegacyFormatToDaysWorked(metadataFormData.employees)
          : [];

        requestData = {
          title: formData.title,
          uploader_id: formData.uploader_id,
          form_type: formData.form_type,
          status: status,
          payrollNo: metadataFormData.payrollNo || 0,
          forWeekEnding: metadataFormData.forWeekEnding || '',
          projectName: metadataFormData.projectName || '',
          contractNo: metadataFormData.contractNo || '',
          employees: employeesForAPI,
          dateSigned: metadataFormData.dateSigned || '',
          signatoryName: metadataFormData.signatoryName || '',
          signatoryTitle: metadataFormData.signatoryTitle || '',
          payrollStartDate: metadataFormData.payrollStartDate || '',
          payrollEndDate: metadataFormData.payrollEndDate || '',
          fringeCheckbox_a: metadataFormData.fringeCheckbox_a || 'unchecked',
          friendCheckbox_b: metadataFormData.friendCheckbox_b || 'unchecked',
          exceptions: metadataFormData.exceptions || [],
          remarks: metadataFormData.remarks || '',
          correction_comments: correctionComments?.trim() || null
        };

        // Only include contractorName OR subcontractorName based on companyType selection (for reprocessing)
        if (metadataFormData.companyType === 'contractor') {
          requestData.contractorName = metadataFormData.contractorName || '';
        } else if (metadataFormData.companyType === 'subcontractor') {
          requestData.subcontractorName = metadataFormData.subcontractorName || '';
        } else {
          // Legacy fallback: if no companyType set, use whichever field has a value
          if (metadataFormData.contractorName) {
            requestData.contractorName = metadataFormData.contractorName;
          } else if (metadataFormData.subcontractorName) {
            requestData.subcontractorName = metadataFormData.subcontractorName;
          }
        }
      } else {
        // For status changes only (Approve/Reject): minimal data, just status update
        requestData = {
          title: formData.title,
          uploader_id: formData.uploader_id,
          form_type: formData.form_type,
          status: status,
          correction_comments: correctionComments?.trim() || null
        };
      }


      // Submit the document with updated status using the PUT /documents endpoint
      const response = await axios.put('/documents', requestData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        const result = response.data;
        const successAction = shouldReprocess ? 'corrected and resubmitted' : 'updated';

        // Always refresh document data first, regardless of success/error status
        // This ensures the modal shows the latest validation state from the server
        if (!shouldCloseModal) {
          try {

            // Give the server a moment to finish processing before fetching the results
            await new Promise(resolve => setTimeout(resolve, 500));

            // Re-fetch document and reviews to show updated status and validation errors
            const [docResponse, metaResponse, reviewsResponse] = await Promise.all([
              axios.get(`/documents/${documentId}`, { headers: getAuthHeaders() }),
              axios.get(`/documents/${documentId}/metadata`, { headers: getAuthHeaders() }),
              axios.get(`/documents/${documentId}/reviews`, { headers: getAuthHeaders() })
            ]);

            if (docResponse.status === 200) {
              const doc = docResponse.data;
              setDocument(doc);
              setFormData({
                title: doc.title || '',
                status: doc.status || '',
                form_type: doc.form_type || '',
                uploader_id: doc.uploader_id || '',
                external_reference_id: doc.external_reference_id || ''
              });
            }

            if (metaResponse.status === 200) {
              setMetadata(metaResponse.data);
              // Update metadata form data with latest values
              if (metaResponse.data.length > 0) {
                const latestMetadata = metaResponse.data[0];
                const fieldData = latestMetadata.field_data || {};

                // Convert daysWorked array to legacy daily hour fields for WH-347 forms
                if (fieldData.employees) {
                  fieldData.employees = convertDaysWorkedToLegacyFormat(
                    fieldData.employees,
                    fieldData.payrollStartDate,
                    fieldData.payrollEndDate
                  );
                }

                // DO NOT overwrite user corrections after resubmit - preserve manual edits
                // Only update if this is NOT after a resubmit operation
                // setMetadataFormData(fieldData); // REMOVED: This was overwriting user corrections
              }
            }

            if (reviewsResponse.status === 200) {
              const reviews = reviewsResponse.data;
              setDocumentReviews(reviews);

              // Force validation state reset after reprocessing - multiple triggers to ensure it works
              setValidationCache(new Map()); // Clear validation cache
              setValidationTrigger(prev => prev + 1);

              // Force a re-render of the review history section by updating the key
              // This ensures the Review History & Comments section refreshes immediately
              setDocumentReviews([...reviews]); // Create new array reference to trigger re-render

              // Clear existing suggestions so new ones can be detected after reprocessing
              setSuggestions({
                contractorName: { visible: false, items: [], originalValue: '' },
                subcontractorName: { visible: false, items: [], originalValue: '' },
                projectName: { visible: false, items: [], originalValue: '' },
                jobTitle: { visible: false, items: [], originalValue: '' }
              });
              
              // Clear all processing states
              suggestionsBeingProcessed.current.clear();

              // Check if the latest review shows success and force additional refresh if so
              if (reviews.length > 0) {
                const latestReview = reviews[0];
                if (latestReview.comments && latestReview.comments.includes('Document verification completed successfully')) {
                  setTimeout(() => {
                    setValidationTrigger(prev => prev + 1);
                  }, 50);
                  setTimeout(() => {
                    setValidationTrigger(prev => prev + 1);
                  }, 200);
                }
              }
            }

            // Clear correction comments and reset metadata changed flag after successful submission
            setCorrectionComments('');
            setMetadataChanged(false);

            // Additional refresh for review history after a short delay to ensure server has processed
            setTimeout(async () => {
              try {
                const delayedReviewsResponse = await axios.get(`/documents/${documentId}/reviews`, { headers: getAuthHeaders() });
                if (delayedReviewsResponse.status === 200) {
                  setDocumentReviews([...delayedReviewsResponse.data]); // Force re-render with fresh data
                }
              } catch (err) {
                console.warn('Delayed review refresh failed:', err);
              }
            }, 1000);

            // Add additional validation triggers to ensure UI updates
            setTimeout(() => {
              setValidationCache(new Map());
              setValidationTrigger(prev => prev + 1);
            }, 100);

            setTimeout(() => {
              setValidationCache(new Map());
              setValidationTrigger(prev => prev + 1);
            }, 1000);

            setTimeout(() => {
              setValidationCache(new Map());
              setValidationTrigger(prev => prev + 1);
            }, 2000);

          } catch (refreshErr) {
            console.error('Error refreshing document data:', refreshErr);
            setError('Document processed, but failed to refresh display data. Please close and reopen the document.');
          }
        }

        // Now handle the processing result and determine whether to close modal
        const processingSuccess = handleProcessingResult(result, successAction, 'update');

        if (shouldCloseModal) {
          // For operations that should close modal (Approve/Reject), close regardless of validation errors
          if (onDocumentUpdated) {
            onDocumentUpdated();
          }
          onClose();
        } else {
          // For reprocessing operations, keep modal open regardless of success/error
          // The refresh above ensures the latest validation state is shown
        }
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'Error updating document'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content edit-modal">
          <div className="modal-header">
            <h2>Edit Document - ID: {documentId}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>Loading document data...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <>
      {/* Auto-calculation prompt modal */}
      {showCalculationPrompt && calculationContext && (
        <div className="modal-overlay calculation-prompt-overlay">
          <div className="modal-content calculation-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Auto-Calculate Values</h3>
              <button
                className="close-button"
                onClick={() => setShowCalculationPrompt(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                You updated <strong>{calculationContext.field}</strong> for employee{' '}
                <strong>{calculationContext.employeeName || `Employee ${calculationContext.employeeIndex + 1}`}</strong>.
              </p>
              <p>
                Would you like to automatically recalculate related payroll values based on this change?
              </p>
              <div className="calculation-preview">
                <strong>Calculated values will include:</strong>
                <ul>
                  <li>Standard wages (Hours × Rate)</li>
                  <li>Overtime wages (Hours × Rate)</li>
                  <li>Net pay (Gross pay - Total deductions)</li>
                </ul>
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    applyCalculatedValues(calculationContext.employeeIndex);
                    setShowCalculationPrompt(false);
                    setCalculationContext(null);
                    setHasBeenPromptedForCalculation(true);
                    setAutoCalculateEnabled(true); // Enable auto-calculation for subsequent changes
                  }}
                >
                  Yes, Auto-Calculate
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCalculationPrompt(false);
                    setCalculationContext(null);
                    setHasBeenPromptedForCalculation(true);
                    setAutoCalculateEnabled(false); // Keep manual values for subsequent changes
                  }}
                >
                  No, Keep Manual Values
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main edit modal */}
      <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Document Review- ID: {documentId}</h2>
          <div className="modal-header-controls">
            <span className="help-tooltip" title="Click section headers to collapse/expand sections. Drag between sections to resize them. Double-click resize handles to reset heights.">
              💡
            </span>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <div className={`edit-content-layout ${isResizing ? 'resizing' : ''}`}>
            {/* Document Preview Section - Left Side */}
            <div
              className="edit-preview-section"
              style={{ flex: `0 0 ${previewWidth}%` }}
            >
              <h3>Document Preview</h3>
              <div className="document-preview-container">
                {previewLoading && (
                  <div className="preview-loading">
                    <p>Loading document preview...</p>
                  </div>
                )}
                {previewError && (
                  <div className="preview-error">
                    <p>{previewError}</p>
                    <button
                      type="button"
                      onClick={() => loadDocumentPreview(documentId)}
                      className="retry-preview-btn"
                    >
                      Retry Loading Preview
                    </button>
                  </div>
                )}
                {documentPreviewUrl && !previewLoading && !previewError && (
                  <div className="document-preview-wrapper">
                    <div className="pdf-viewer-container">
                      <object
                        data={documentPreviewUrl}
                        type="application/pdf"
                        className="document-preview-embed"
                        title="Document Preview"
                      >
                        <embed
                          src={documentPreviewUrl}
                          type="application/pdf"
                          className="document-preview-embed"
                        />
                        <div className="pdf-fallback-message">
                          <p>PDF preview is not supported in this browser.</p>
                          <p>Please use the "Open in New Tab" button below to view the document.</p>
                        </div>
                      </object>
                    </div>
                    <div className="preview-fallback-actions">
                      <button
                        type="button"
                        onClick={() => window.open(documentPreviewUrl, '_blank')}
                        className="open-pdf-btn"
                        title="Open PDF in new tab"
                      >
                        Open in New Tab
                      </button>
                      <a
                        href={documentPreviewUrl}
                        download={`document-${documentId}.pdf`}
                        className="download-pdf-btn"
                        title="Download PDF file"
                      >
                        Download PDF
                      </a>
                    </div>
                  </div>
                )}
                {!documentPreviewUrl && !previewLoading && !previewError && document && (
                  <div className="no-preview">
                    <p>No preview available for this document.</p>
                    {document.file_path && (
                      <button
                        type="button"
                        onClick={() => loadDocumentPreview(documentId)}
                        className="load-preview-btn"
                      >
                        Load Preview
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Resize Handle */}
            <div
              className="resize-handle"
              onMouseDown={handleMouseDown}
              style={{ cursor: isResizing ? 'col-resize' : 'col-resize' }}
            >
              <div className="resize-handle-line"></div>
            </div>

            {/* Document Form Section - Right Side */}
            <div
              className="edit-form-section"
              style={{ flex: `1 1 ${100 - previewWidth}%` }}
            >
              <form className="edit-form">
            <div className="form-section document-info-section">
              <div className="section-header" onClick={() => toggleSection('documentInfo')}>
                <h3>Document Information</h3>
                <span className={`collapse-icon ${collapsedSections.documentInfo ? 'collapsed' : ''}`}>▼</span>
              </div>
              {!collapsedSections.documentInfo && (
                <div className="section-content" style={{
                  height: sectionHeights.documentInfo,
                  overflow: sectionHeights.documentInfo !== 'auto' ? 'auto' : 'visible'
                }}>

              <div className="document-info-grid">
                <div className="form-group">
                  <label htmlFor="title">Document Title:</label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Form Type:</label>
                  <div className="readonly-field">
                    {formData.form_type || 'Not specified'}
                  </div>
                </div>

                <div className="form-group">
                  <label>Current Status:</label>
                  <div className="readonly-field status-display">
                    <span className={`status-badge ${formData.status?.toLowerCase()}`}>
                      {formData.status || 'Unknown'}
                    </span>
                  </div>
                </div>

                {/* Parsing Notes Section */}
                {documentReviews?.[0]?.parsing_notes && documentReviews[0].parsing_notes.length > 0 && (
                  <div className="form-group parsing-notes-group">
                    <label>OCR Parsing Notes:</label>
                    <div className="parsing-notes-container">
                      {documentReviews[0].parsing_notes.map((note, index) => {
                        // Determine note type for styling
                        const noteType = note.toLowerCase().includes('error') ? 'error' :
                                        note.toLowerCase().includes('warning') ? 'warning' :
                                        note.toLowerCase().includes('success') ? 'success' : 'info';

                        return (
                          <div key={index} className={`parsing-note parsing-note-${noteType}`}>
                            <span className="note-index">{index + 1}.</span>
                            <span className="note-text">{note}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="uploader_id">Uploader ID:</label>
                  <input
                    id="uploader_id"
                    type="text"
                    value={formData.uploader_id}
                    onChange={(e) => handleInputChange('uploader_id', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group document-info-full-width">
                  <label htmlFor="external_reference_id">External Reference ID:</label>
                  <input
                    id="external_reference_id"
                    type="text"
                    value={formData.external_reference_id}
                    onChange={(e) => handleInputChange('external_reference_id', e.target.value)}
                  />
                </div>
              </div>

              {documentReviews && documentReviews.length > 0 && (
                <div className="form-group document-info-full-width">
                  <label>Review History & Comments:</label>
                  <div className="review-history">
                    {documentReviews.map((review, index) => (
                      <div key={`${review.review_id}-${review.review_date}-${index}`} className="review-entry">
                        <div className="review-header">
                          <span className="review-status">
                            {review.previous_status} → {review.new_status}
                          </span>
                          <span className="review-date">
                            {new Date(review.review_date).toLocaleString()}
                          </span>
                        </div>
                        {review.comments && (
                          <div className="review-comments">
                            {renderFormattedComments(review.comments)}
                          </div>
                        )}
                        {review.correction_comments && (
                          <div className="correction-comments">
                            <strong>Correction Comments:</strong>
                            {(() => {
                              try {
                                // Try to parse as JSON array
                                const comments = JSON.parse(review.correction_comments);
                                if (Array.isArray(comments)) {
                                  return (
                                    <div className="correction-comments-list">
                                      {comments.map((comment, idx) => {
                                        // Skip the default API generation comment
                                        if (typeof comment === 'string' && comment.includes('Generated by using the API')) {
                                          return null;
                                        }
                                        
                                        return (
                                          <div key={idx} className="correction-comment-item">
                                            <div className="comment-header">
                                              <span className="comment-user">
                                                {typeof comment === 'object' ? (comment.user || 'User') : 'User'}
                                              </span>
                                              {typeof comment === 'object' && comment.timestamp && (
                                                <span className="comment-timestamp">
                                                  {new Date(comment.timestamp).toLocaleString()}
                                                </span>
                                              )}
                                            </div>
                                            <div className="comment-text">
                                              {typeof comment === 'object' ? comment.comment : comment}
                                            </div>
                                          </div>
                                        );
                                      }).filter(Boolean)}
                                    </div>
                                  );
                                } else {
                                  // Not an array, display as single comment (skip API generation comments)
                                  if (review.correction_comments.includes('Generated by using the API')) {
                                    return null;
                                  }
                                  return <span> {review.correction_comments}</span>;
                                }
                              } catch {
                                // JSON parsing failed, display as plain text (skip API generation comments)
                                if (review.correction_comments.includes('Generated by using the API')) {
                                  return null;
                                }
                                return <span> {review.correction_comments}</span>;
                              }
                            })()}
                          </div>
                        )}
                        {review.reviewer_id && (
                          <div className="reviewer-info">
                            <small>Reviewer: {review.reviewer_id}</small>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
                </div>
              )}
              {!collapsedSections.documentInfo && sectionHeights.documentInfo !== 'auto' && (
                <div
                  className="section-resize-handle"
                  onMouseDown={(e) => handleSectionResizeStart('documentInfo', e)}
                  onDoubleClick={() => handleSectionResizeDoubleClick('documentInfo')}
                  title="Drag to resize, double-click to reset height"
                >
                  <div className="section-resize-line"></div>
                </div>
              )}
            </div>

            <div className="form-section payroll-form-section">
              <div className="section-header" onClick={() => toggleSection('payrollForm')}>
                <h3>Document Form Data (Editable)</h3>
                <span className={`collapse-icon ${collapsedSections.payrollForm ? 'collapsed' : ''}`}>▼</span>
              </div>
              {!collapsedSections.payrollForm && (
                <div className="section-content" style={{
                  height: sectionHeights.payrollForm,
                  overflow: sectionHeights.payrollForm !== 'auto' ? 'auto' : 'visible'
                }}>
                  <div className="metadata-form">
                    {renderWH347Form()}
                  </div>
                </div>
              )}
              {!collapsedSections.payrollForm && sectionHeights.payrollForm !== 'auto' && (
                <div
                  className="section-resize-handle"
                  onMouseDown={(e) => handleSectionResizeStart('payrollForm', e)}
                  onDoubleClick={() => handleSectionResizeDoubleClick('payrollForm')}
                  title="Drag to resize, double-click to reset height"
                >
                  <div className="section-resize-line"></div>
                </div>
              )}
            </div>


            <div className="form-section correction-comments-section">
              <div className="section-header" onClick={() => toggleSection('correctionComments')}>
                <h3>Correction Comments & Results</h3>
                <span className={`collapse-icon ${collapsedSections.correctionComments ? 'collapsed' : ''}`}>▼</span>
              </div>
              {!collapsedSections.correctionComments && (
                <div className="section-content" style={{
                  height: sectionHeights.correctionComments,
                  overflow: sectionHeights.correctionComments !== 'auto' ? 'auto' : 'visible'
                }}>
                  <div className="form-group correction-comments-field">
                <label htmlFor="correction_comments">Correction Comments:</label>
                <textarea
                  id="correction_comments"
                  value={correctionComments}
                  onChange={(e) => setCorrectionComments(e.target.value)}
                  placeholder="Describe what corrections you made to fix processing issues (required for Reject actions, optional for approval)..."
                  rows={3}
                />
                <small className="help-text">
                  <strong>Required for Reject actions.</strong> Describe what corrections were made or reasons for rejection. Any metadata changes are automatically saved when you approve the document.
                </small>
                
                {/* Save Correction Comments Button */}
                <div className="save-comments-section">
                  <button
                    type="button"
                    onClick={handleSaveCorrectionComments}
                    disabled={savingComments || !correctionComments?.trim()}
                    className="save-comments-btn"
                    title={!correctionComments?.trim() ? 'Enter correction comments to save' : 'Save correction comments for later reference'}
                  >
                    {savingComments ? 'Saving...' : 'Save Correction Comments'}
                  </button>
                </div>
              </div>

              {/* Result Display - Simple success messages only (detailed validation shown in Review History) */}
              {error && error.type === 'success' && (
                <div className="upload-result success">
                  <span className="result-icon">✅</span>
                  <div className="result-message">
                    <div><strong>✅ Operation Successful</strong></div>
                    <div className="result-details">
                      {error.message}
                    </div>
                  </div>
                </div>
              )}
                </div>
              )}
              {!collapsedSections.correctionComments && sectionHeights.correctionComments !== 'auto' && (
                <div
                  className="section-resize-handle"
                  onMouseDown={(e) => handleSectionResizeStart('correctionComments', e)}
                  onDoubleClick={() => handleSectionResizeDoubleClick('correctionComments')}
                  title="Drag to resize, double-click to reset height"
                >
                  <div className="section-resize-line"></div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" onClick={onClose} className="cancel-btn">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={buttonStates.reject || !correctionComments?.trim()}
                className="reject-btn"
                title={!correctionComments?.trim() ? 'Correction comments are required for rejection' : 'Reject document'}
              >
                {buttonStates.reject ? 'Processing...' : 'Reject'}
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={buttonStates.approve || formData.status === 'rejected'}
                className="approve-btn"
                title={formData.status === 'rejected' ? 'Cannot approve rejected documents' : 'Save corrections and approve document, then process employees'}
              >
                {buttonStates.approve ? 'Processing...' : 'Approve & Process Employees'}
              </button>
            </div>

            {/* Status display near Approve button */}
            {error && (
              <div className={`button-result ${error.type === 'success' ? 'success' : 'error'}`}>
                <span className="result-icon">{error.type === 'success' ? '✅' : '❌'}</span>
                <div className="result-message">
                  {error.type === 'validation' ? (
                    <>
                      <div><strong>📋 Validation Errors</strong></div>
                      <div className="result-details">
                        {error.parsedData && error.parsedData.issueCount > 0 && (
                          <div>⚠️ {error.parsedData.issueCount} validation issue{error.parsedData.issueCount > 1 ? 's' : ''} found</div>
                        )}
                        <div>Please check the Review History & Comments section below for detailed information.</div>
                      </div>
                    </>
                  ) : (
                    <span>{error.message}</span>
                  )}
                </div>
              </div>
            )}
          </form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default EditDocument;